import { useLayoutEffect, useRef, type RefObject } from 'react';
import type { UiLocale } from '../../app/ui-locale';

const ENGLISH_COPY: Record<string, string> = {
  // Common actions and states.
  'و': 'and',
  'یا': 'or',
  'بستن': 'Close',
  'حذف': 'Delete',
  'ویرایش': 'Edit',
  'تکثیر': 'Duplicate',
  'ذخیره': 'Save',
  'ذخیره تغییرات': 'Save changes',
  'در حال ذخیره…': 'Saving…',
  'در حال حذف…': 'Deleting…',
  'در حال انجام…': 'Working…',
  'در حال تکثیر…': 'Duplicating…',
  'در حال بارگذاری…': 'Loading…',
  'در حال دریافت…': 'Loading…',
  'در حال دریافت فرم…': 'Loading form…',
  'بازخوانی': 'Refresh',
  'بازگشت': 'Back',
  'بازگشت به فهرست فرم‌ها': 'Back to forms',
  'بازگشت به سایت': 'Back to website',
  'پیش‌نمایش': 'Preview',
  'پیش‌نمایش فرم': 'Form preview',
  'بررسی': 'Validate',
  'بررسی ساختار فرم': 'Validate form structure',
  'تاریخچه تغییرات': 'Change history',
  'بازگردانی (Ctrl+Z)': 'Undo (Ctrl+Z)',
  'انجام مجدد (Ctrl+Y)': 'Redo (Ctrl+Y)',
  'ذخیره (Ctrl+S)': 'Save (Ctrl+S)',
  'ذخیره‌شده': 'Saved',
  'ذخیره‌نشده': 'Unsaved',
  'فعال': 'Active',
  'غیرفعال': 'Inactive',
  'خطا': 'Error',
  'هشدار': 'Warning',
  'تنظیمات': 'Settings',
  'ابزارها': 'Tools',
  'فرم': 'Form',
  'عنصر': 'Element',
  'صفحه': 'Page',
  'صفحه اول': 'First page',
  'صفحه قبل': 'Previous page',
  'صفحه بعد': 'Next page',
  'صفحه آخر': 'Last page',
  'انتخاب کنید': 'Select…',
  'نتیجه‌ای یافت نشد.': 'No results found.',
  'خطای ناشناخته': 'Unknown error',

  // Designer toolbar and document.
  'فرم جدید': 'New form',
  'طراحی فرم': 'Form design',
  'طراحی فرم جدید': 'Design new form',
  'ویرایش فرم': 'Edit form',
  'فرم‌ساز': 'Form builder',
  'ابزارهای طراحی فرم': 'Form design tools',
  'عرض پیش‌نمایش': 'Preview width',
  'همه تغییرات ذخیره شده است': 'All changes are saved',
  'تغییرات ذخیره‌نشده وجود دارد': 'There are unsaved changes',
  'ساختار فرم بررسی شد و مشکل شناخته‌شده‌ای پیدا نشد.': 'The form structure is valid and no known issue was found.',
  'برای رفتن به محل هر مورد روی آن کلیک کنید.': 'Select an item to navigate to its location.',
  'بستن پیش‌نمایش': 'Close preview',
  'فرم بدون عنوان': 'Untitled form',

  // Palette and canvas.
  'جستجوی کنترل…': 'Search controls…',
  'جستجوی کنترل': 'Search controls',
  'پاک‌کردن جستجو': 'Clear search',
  'لایه‌ها': 'Layouts',
  'درخت لایه‌ها': 'Layer tree',
  'کنترل‌های فرم': 'Form controls',
  'کنترل‌های عمومی': 'General controls',
  'کنترل را اینجا رها کنید': 'Drop a control here',
  'رها کنید': 'Drop here',
  'لایه را داخل پنل رها کنید': 'Drop the layout inside the panel',
  'لایه را در انتهای پنل قرار دهید': 'Place the layout at the end of the panel',
  'لایه یا کنترل نمایشی را اینجا رها کنید': 'Drop a layout or display control here',
  'لایه را در انتهای بخش ورود ردیف قرار دهید': 'Place the layout at the end of the row-entry area',
  'انتقال کنترل به انتهای ستون': 'Move control to end of column',
  'برای جابه‌جایی بکشید': 'Drag to move',
  'با کلیک اضافه کنید یا به محل دلخواه بکشید.': 'Click to add, or drag to the desired location.',
  'کنترل یا لایه را اینجا رها کنید': 'Drop a control or layout here',
  'برای انتقال لایه به انتهای مرحله، آن را اینجا رها کنید.': 'Drop the layout here to move it to the end of the step.',
  'جابه‌جایی کنترل': 'Move control',
  'جابه‌جایی لایه': 'Move layout',
  'تکثیر کنترل': 'Duplicate control',
  'تکثیر لایه': 'Duplicate layout',
  'حذف کنترل': 'Delete control',
  'حذف لایه': 'Delete layout',
  '۱ ستون': '1 column',
  '۲ ستون': '2 columns',
  '۳ ستون': '3 columns',
  '۴ ستون': '4 columns',
  '1 ستون': '1 column',
  '2 ستون': '2 columns',
  '3 ستون': '3 columns',
  '4 ستون': '4 columns',

  // Control names.
  'جعبه متنی': 'Text box',
  'ناحیه متنی': 'Text area',
  'جعبه کشویی': 'Dropdown',
  'چک‌باکس': 'Checkbox',
  'رادیوباکس': 'Radio buttons',
  'فیلد عددی': 'Number field',
  'آپلود فایل': 'File upload',
  'جدول اطلاعاتی': 'Data table',
  'پنل': 'Panel',
  'تصویر': 'Image',
  'متن و HTML': 'Text and HTML',
  'فاصله': 'Spacer',
  'فاصله نمایشی': 'Spacer',
  'دکمه': 'Button',
  'کد امنیتی': 'Security code',
  'برچسب متصل': 'Bound label',
  'تاریخ و زمان': 'Date and time',
  'انتخابگر تاریخ و زمان': 'Date and time picker',
  'پیشرفت نمونه': 'Sample progress',

  // Form settings.
  'تنظیمات فرم': 'Form settings',
  'عنوان فرم': 'Form title',
  'نام فنی': 'Technical name',
  'نام فنی فرم': 'Form technical name',
  'توضیحات': 'Description',
  'فرم فعال باشد': 'Form is active',
  'بدون محدودیت زمانی': 'No time limit',
  'تاریخ شروع': 'Start date',
  'تاریخ پایان': 'End date',
  'پیام عدم دسترسی/انقضا': 'Unavailable / expired message',
  'ظرفیت محدود': 'Limited capacity',
  'ظرفیت': 'Capacity',
  'پیام ظرفیت': 'Capacity message',
  'نمایش پیام نهایی': 'Show final message',
  'نمایش کد پیگیری': 'Show tracking code',
  'گیرندگان پیامک': 'SMS recipients',
  'مرحله‌ها': 'Steps',
  'مراحل فرم': 'Form steps',
  'افزودن مرحله': 'Add step',
  'عنوان مرحله': 'Step title',
  'نام فنی مرحله': 'Step technical name',
  'امکان بازگشت': 'Allow going back',
  'پیش‌نمایش قبل از ادامه': 'Preview before continuing',
  'وضعیت‌های پاسخ': 'Submission statuses',
  'افزودن وضعیت': 'Add status',
  'عنوان وضعیت': 'Status title',
  'گیرنده': 'Recipient',
  'تنظیمات نمایش سربرگ فرم': 'Form heading display settings',
  'تراز عنوان': 'Title alignment',
  'پیش‌فرض قالب سایت': 'Use site theme default',
  'پیش‌فرض زبان': 'Language default',
  'رنگ عنوان': 'Title color',
  'رنگ عنوان؛ انتخاب رنگ': 'Title color; choose a color',
  'نمایش کامل بخش سربرگ فرم': 'Show the complete form heading block',
  'رنگ پس‌زمینه سربرگ': 'Heading background color',
  'رنگ پس‌زمینه سربرگ؛ انتخاب رنگ': 'Heading background color; choose a color',
  'پیش‌نمایش عنوان فرم': 'Form title preview',
  'نمایش بازه زمانی در سربرگ فرم': 'Show the active date range in the form heading',
  'مخفی‌کردن کامل سربرگ فقط عنوان، توضیحات، بازه زمانی و وضعیت ظرفیت را از صفحه عمومی حذف می‌کند؛ محدودیت‌های تاریخ و ظرفیت همچنان در زمان ثبت اجرا می‌شوند.': 'Hiding the complete heading removes only its title, description, active date range and capacity status from the public page; date and capacity rules are still enforced on submission.',
  'بازنشانی ستون‌ها': 'Reset columns',
  'ستون‌ها بازنشانی شدند؛ برای مشاهده نتایج، اعمال فیلتر را بزنید.': 'The columns were reset. Apply the filter to refresh the results.',
  'فیلدهای فرم هنوز برای فیلتر و ستون‌ها آماده نیستند.': 'The form fields are not ready for filters and columns yet.',
  'ابتدا منتظر دریافت فیلدهای فرم بمانید.': 'Wait until the form fields have been loaded.',
  'تعریف فرم برای فیلتر و ستون‌ها دریافت نشد:': 'The form definition for filters and columns could not be loaded:',

  // Property editor sections and common fields.
  'تنظیمات کنترل': 'Control settings',
  'ویژگی‌ها': 'Properties',
  'اعتبارسنجی': 'Validation',
  'شرط نمایش': 'Visibility rule',
  'پیشرفته': 'Advanced',
  'عنوان کنترل': 'Control label',
  'نام فنی مدل': 'Model name',
  'نام فنی مدل *': 'Model name *',
  'کلید فنی داده': 'Data key',
  'کلید فنی فرم': 'Form key',
  'کپی': 'Copy',
  'نمایش': 'Show',
  'تنظیمات پیشرفته فرم': 'Advanced form settings',
  'کلید فنی جدید': 'New data key',
  'کلید فنی جدید فرم': 'New form key',
  'فعال‌کردن ویرایش تخصصی کلید فنی': 'Enable expert data-key editing',
  'فعال‌کردن ویرایش تخصصی کلید فرم': 'Enable expert form-key editing',
  'اعمال کلید جدید': 'Apply new key',
  'شناسه داخلی این فیلد برای ذخیره پاسخ، گزارش، فیلتر، شرط نمایش و اتصال کنترل‌ها است.': 'This field’s internal identifier is used for stored responses, reports, filters, visibility rules and control bindings.',
  'شناسه داخلی فرم و پیشوند مسیرهای داده، فیلترها و گزارش‌ها است. پس از اولین ذخیره نباید تغییر کند.': 'This is the form’s internal identifier and the prefix for data paths, filters and reports. It must not change after the first save.',
  'این فرم قبلاً ذخیره شده است. تغییر کلید فنی از این صفحه غیرفعال است و فقط باید همراه Migration داده انجام شود.': 'This form has already been saved. Changing the data key is disabled here and must only be done together with a data migration.',
  'این فرم قبلاً ذخیره شده است. تغییر کلید فنی فرم از این صفحه غیرفعال است و فقط باید همراه Migration داده انجام شود.': 'This form has already been saved. Changing the form key is disabled here and must only be done together with a data migration.',
  'پس از ساخت شرط، اتصال یا گزارش، تغییر این مقدار می‌تواند وابستگی‌ها را نامعتبر کند.': 'Changing this value after creating rules, bindings or reports may invalidate those dependencies.',
  'پس از ذخیره فرم یا ساخت فیلتر و گزارش، تغییر این مقدار می‌تواند وابستگی‌ها را نامعتبر کند.': 'Changing this value after saving the form or creating filters and reports may invalidate those dependencies.',
  'کلید باید با حرف انگلیسی شروع شود و فقط شامل حروف، عدد و زیرخط باشد.': 'The key must start with an English letter and contain only letters, numbers and underscores.',
  'کلید فرم باید با حرف انگلیسی شروع شود و فقط شامل حروف، عدد و زیرخط باشد.': 'The form key must start with an English letter and contain only letters, numbers and underscores.',
  'این کلید در محدوده همین مرحله یا جدول تکراری است.': 'This key is duplicated in the same step or table scope.',
  'ID کنترل': 'Control ID',
  'متن راهنما (Placeholder)': 'Placeholder',
  'نوع ورودی': 'Input type',
  'مقدار پیش‌فرض': 'Default value',
  'مقدار نامعتبر': 'Invalid value',
  'اندازه': 'Size',
  'اندازه متن': 'Text size',
  'چینش': 'Alignment',
  'تراز متن': 'Text alignment',
  'نوع نمایش': 'Display mode',
  'عرض کامل': 'Block',
  'درون‌خطی': 'Inline',
  'راست‌چین': 'Right',
  'وسط': 'Center',
  'وسط‌چین': 'Center',
  'چپ‌چین': 'Left',
  'تراز دوطرفه': 'Justify',
  'عادی': 'Normal',
  'درشت و برجسته': 'Large and bold',
  'رنگ': 'Color',
  'رنگ متن': 'Text color',
  'رنگ سربرگ پنل': 'Panel header color',
  'رنگ متن سربرگ': 'Header text color',
  'رنگ زمینه بدنه پنل': 'Panel body background',
  'رنگ کادر': 'Border color',
  'قالب رنگی آماده': 'Preset color style',
  'بدون مدل': 'No model',
  'فیلد مبنا': 'Source field',
  'فیلد متصل': 'Bound field',
  'مقدار مقایسه': 'Comparison value',
  'عملگر': 'Operator',
  'عنوان': 'Title',
  'متن دکمه': 'Button text',
  'عملکرد': 'Action',
  'محتوای HTML': 'HTML content',
  'آدرس تصویر': 'Image URL',
  'متن جایگزین': 'Alternative text',
  'عرض (px)': 'Width (px)',
  'ارتفاع (px)': 'Height (px)',
  'ارتفاع فاصله (پیکسل)': 'Spacer height (pixels)',
  'فیلتر نمایش': 'Display filter',
  'پیشوند': 'Prefix',
  'پسوند': 'Suffix',
  'Style کنترل': 'Control style',
  'کلاس‌های CSS کنترل': 'Control CSS classes',
  'Attributeهای کنترل': 'Control attributes',
  'کلاس‌های CSS Wrapper': 'Wrapper CSS classes',
  'Attributeهای Wrapper': 'Wrapper attributes',
  'HTML قبل از کنترل': 'HTML before control',
  'HTML بعد از کنترل': 'HTML after control',
  'CSS class عمومی (Legacy)': 'Global CSS class (legacy)',
  'نمایشگرها': 'Display settings',

  // Options and validation.
  'گزینه‌ها': 'Options',
  'افزودن گزینه': 'Add option',
  'متن گزینه اولیه': 'Initial option text',
  'گزینه غیرفعال': 'Disabled option',
  'فیلد الزامی': 'Required field',
  'پیام فیلد الزامی': 'Required message',
  'حداقل طول': 'Minimum length',
  'حداکثر طول': 'Maximum length',
  'پیام حداقل طول': 'Minimum-length message',
  'پیام حداکثر طول': 'Maximum-length message',
  'حداقل مقدار': 'Minimum value',
  'حداکثر مقدار': 'Maximum value',
  'پیام کمتر از حداقل': 'Below-minimum message',
  'پیام بیشتر از حداکثر': 'Above-maximum message',
  'گام عددی (Step)': 'Numeric step',
  'پیام گام نامعتبر': 'Invalid-step message',
  'عبارت منظم (Regex)': 'Regular expression (Regex)',
  'پیام Regex': 'Regex message',
  'پیام Mask': 'Mask message',
  'آدرس اعتبارسنجی Remote': 'Remote validation URL',
  'پیام Remote': 'Remote validation message',
  'مقدار یکتا باشد': 'Value must be unique',
  'پیام مقدار تکراری': 'Duplicate-value message',
  'نوع/پسوندهای مجاز': 'Allowed types / extensions',
  'پیام نوع فایل نامعتبر': 'Invalid-file-type message',
  'حداقل حجم (KB)': 'Minimum size (KB)',
  'حداکثر حجم (KB)': 'Maximum size (KB)',
  'پیام حداقل حجم': 'Minimum-size message',
  'پیام حداکثر حجم': 'Maximum-size message',
  'حداقل ردیف': 'Minimum rows',
  'پیام حداقل ردیف': 'Minimum-row message',
  'طول کد': 'Code length',
  'شامل حرف باشد': 'Include letters',
  'کلید عمومی reCAPTCHA': 'reCAPTCHA public key',

  // Layout editor.
  'تنظیمات لایه': 'Layout settings',
  'ستون‌ها': 'Columns',
  'عرض ستون': 'Column width',
  'مجموع عرض': 'Total width',
  'توزیع مساوی': 'Distribute evenly',
  'مجموع عرض ستون‌ها باید ۱۲ باشد.': 'The total column span must be 12.',
  'فضای خالی': 'Unused space',

  // Submissions and filters.
  'پاسخ‌های ثبت‌شده': 'Submissions',
  'پاسخ ثبت‌شده': 'Submission',
  'انتخاب فرم': 'Select form',
  'فقط دارای پاسخ': 'Only with submissions',
  'فقط فرم‌های فعال': 'Active forms only',
  'فقط منقضی‌نشده': 'Not expired only',
  'اعمال فیلتر': 'Apply filters',
  'پاک کردن فیلترها': 'Clear filters',
  'پاک‌کردن فیلترها': 'Clear filters',
  'فیلترها': 'Filters',
  'پرس‌وجوهای ذخیره‌شده': 'Saved queries',
  'نام پرس‌وجو': 'Query name',
  'ذخیره پرس‌وجو': 'Save query',
  'مرتب‌سازی': 'Sort by',
  'جهت مرتب‌سازی': 'Sort direction',
  'صعودی': 'Ascending',
  'نزولی': 'Descending',
  'تعداد در صفحه': 'Page size',
  'جستجوی عنوان': 'Search title',
  'اعمال': 'Apply',
  'خروجی Excel': 'Export to Excel',
  'تغییر وضعیت': 'Change status',
  'تغییر وضعیت پاسخ': 'Change submission status',
  'وضعیت': 'Status',
  'یادداشت وضعیت': 'Status note',
  'ستون‌های سیستمی': 'System columns',
  'کد ثبت': 'Submission code',
  'تاریخ ثبت': 'Submission date',
  'تاریخ': 'Date',
  'نتایج': 'Results',
  'ستون انتخابی از فیلدهای فرم وجود ندارد.': 'No form-field display column is selected.',

  // Reports.
  'گزارش‌ساز': 'Report builder',
  'گزارش‌های سفارشی': 'Custom reports',
  'گزارش جدید': 'New report',
  'ایجاد گزارش': 'Create report',
  'ویرایش گزارش': 'Edit report',
  'ذخیره گزارش': 'Save report',
  'انتخاب گزارش': 'Select report',
  'عنوان گزارش': 'Report title',
  'قالب گزارش': 'Report template',
  'سربرگ': 'Header',
  'بدنه': 'Body',
  'پابرگ': 'Footer',
  'اتصال به فایل فرم': 'Bind to form field',
  'مدیریت گزارش': 'Report management',
  'طراحی قالب و گزارش': 'Design templates and reports',

  // Site UI settings.
  'تنظیمات نمایش': 'Display settings',
  'انتخاب نسخه Bootstrap': 'Select Bootstrap version',
  'ذخیره برای این سایت': 'Save for this site',
  'قالب‌های قدیمی DNN': 'Legacy DNN themes',
  'قالب‌های جدید و مدرن': 'Modern themes',

  // Messages and confirmations.
  'فرم ایجاد شد.': 'Form created.',
  'فرم حذف شد.': 'Form deleted.',
  'فرم تکثیر شد.': 'Form duplicated.',
  'تغییرات فرم ذخیره شد.': 'Form changes saved.',
  'سرور ذخیره فرم را تأیید نکرد.': 'The server did not confirm saving the form.',
  'خطا در ذخیره فرم': 'Unable to save the form',
  'حذف فرم ناموفق بود.': 'Unable to delete the form.',
  'گزارش ذخیره شد.': 'Report saved.',
  'گزارش حذف شد.': 'Report deleted.',
  'خطا در ذخیره گزارش': 'Unable to save the report',
  'خطا در حذف گزارش': 'Unable to delete the report',
  'پاسخ حذف شد.': 'Submission deleted.',
  'خطا در حذف پاسخ': 'Unable to delete the submission',
  'وضعیت ذخیره شد.': 'Status saved.',
  'خطا در ذخیره وضعیت': 'Unable to save status',
  'خطا در دریافت وضعیت': 'Unable to load status',
  'پرس‌وجو ذخیره شد.': 'Query saved.',
  'پرس‌وجو حذف شد.': 'Query deleted.',
  'خطا در ذخیره پرس‌وجو': 'Unable to save the query',
  'خطا در حذف پرس‌وجو': 'Unable to delete the query',
  'ابتدا یک فرم انتخاب کنید.': 'Select a form first.',
  'عنوان گزارش را وارد کنید.': 'Enter a report title.',
  'نام پرس‌وجو را وارد کنید.': 'Enter a query name.',
  'تاریخ شروع بازه معتبر نیست.': 'The range start date is invalid.',
  'تاریخ پایان بازه معتبر نیست.': 'The range end date is invalid.',
  'تاریخ پایان بازه نمی‌تواند قبل از تاریخ شروع باشد.': 'The range end date cannot be before the start date.',
  'تغییرات ذخیره‌نشده از بین برود؟': 'Discard unsaved changes?',
  'فرم و اطلاعات طراحی آن حذف شود؟': 'Delete the form and its design data?',
  'این پاسخ حذف شود؟': 'Delete this submission?',
  'این گزارش حذف شود؟': 'Delete this report?',
  'گزارش حذف شود؟': 'Delete the report?',
  'این مرحله حذف شود؟': 'Delete this step?',
  'پرس‌وجوی ذخیره‌شده حذف شود؟': 'Delete the saved query?',
  'فرم جدیدی را از صفر طراحی کنید.': 'Design a new form from scratch.',
  'فرمی با این فیلترها پیدا نشد.': 'No form matched these filters.',
  'فیلترها را پاک کنید یا یک فرم جدید بسازید.': 'Clear filters or create a new form.',
  'توضیحی برای این فرم ثبت نشده است.': 'No description has been entered for this form.',

  // Search placeholders and helper copy.
  'جستجو در عنوان فرم‌ها…': 'Search form titles…',
  'فرم‌ها را جستجو، مدیریت و برای دریافت پاسخ آماده کنید.': 'Search, manage, and prepare forms to receive submissions.',
  'فرم‌های شما در یک نگاه': 'All your forms at a glance',
  'فرم‌های نمایش‌داده‌شده': 'Displayed forms',
  'داشبورد مدیریتی': 'Management dashboard',
  'مدیریت هوشمند فرم‌ها': 'Smart form management',
  'نمای کلی و مدیریت فرم‌ها': 'Overview and form management',
  'ساخت و ویرایش فرم': 'Create and edit forms',
  'فیلتر، وضعیت و خروجی': 'Filter, status and export',
  'ظاهر و سازگاری فرم‌ها': 'Appearance and compatibility',
  'خروج از محیط مدیریت': 'Leave administration'

  , 'زبان': 'Language'
  , 'شماره': 'Number'
  , 'شناسه': 'ID'
  , 'فارسی': 'Persian'
  , 'مقدار': 'Value'
  , '۱۲۳٫۵': '123.5'
  , 'در صفحه': 'per page'
  , 'نامحدود': 'Unlimited'
  , 'فرم فعال': 'Active form'
  , 'بستن پیام': 'Close message'
  , 'تعداد ثبت': 'Submission count'
  , 'صفحه‌بندی': 'Pagination'
  , 'منوی اصلی': 'Main menu'
  , ' (زیرگروه)': ' (subgroup)'
  , 'آپلود تصویر': 'Upload image'
  , 'بازه فعالیت': 'Active period'
  , 'تاریخ ایجاد': 'Created date'
  , 'مدیریت داده': 'Data management'
  , 'مرکز مدیریت': 'Administration center'
  , 'تولید کد جدید': 'Generate new code'
  , 'ساخت فرم تازه': 'Create a new form'
  , 'نمونه توضیحات': 'Description example'
  , 'نمونه دکمه‌ها': 'Button examples'
  , 'ایجاد فرم جدید': 'Create new form'
  , 'داشبورد فرم‌ها': 'Forms dashboard'
  , 'تصویر آپلود شد.': 'Image uploaded.'
  , 'اعتبارسنجی آماده': 'Built-in validation'
  , 'بستن منوی مدیریت': 'Close administration menu'
  , 'خطا در تکثیر فرم': 'Unable to duplicate form'
  , 'طراحی گزارش جدید': 'Design new report'
  , 'نمونه ورودی متنی': 'Text input example'
  , 'image picture عکس': 'image picture photo'
  , 'دارای ظرفیت محدود': 'Limited capacity'
  , 'radio option گزینه': 'radio option'
  , 'خطا در آپلود تصویر': 'Unable to upload image'
  , 'باز کردن منوی مدیریت': 'Open administration menu'
  , 'جمع کردن منوی مدیریت': 'Collapse administration menu'
  , 'check boolean بله خیر': 'check boolean yes no'
  , 'recaptcha google کپچا': 'reCAPTCHA Google captcha'
  , 'space separator فاصله': 'space separator spacer'
  , 'عنوان فرم الزامی است.': 'Form title is required.'
  , 'نمونه تصویر واکنش‌گرا': 'Responsive image example'
  , 'پیام اعتبارسنجی آماده': 'Built-in validation message'
  , 'captcha security امنیتی': 'captcha security code'
  , 'خطا در بارگذاری پرس‌وجو': 'Unable to load the query'
  , 'نام فنی فرم الزامی است.': 'Form technical name is required.'
  , 'table grid row جدول ردیف': 'table grid row'
  , 'date time clock تاریخ زمان': 'date time clock'
  , 'panel group container گروه': 'panel group container'
  , 'select dropdown list انتخاب': 'select dropdown list'
  , 'input textbox text متن ورودی': 'input textbox text'
  , 'label bind value برچسب مقدار': 'label bind value'
  , 'paragraph html rich text متن': 'paragraph HTML rich text'
  , 'ظرفیت فرم باید حداقل ۱ باشد.': 'Form capacity must be at least 1.'
  , 'یک لایه بدون ستون وجود دارد.': 'A layout has no columns.'
  , 'textarea multiline متن چندخطی': 'textarea multiline text'
  , 'فیلد و مقدار شرط را مشخص کنید.': 'Select the source field and condition value.'
  , 'نام فنی یکی از مراحل خالی است.': 'A step technical name is empty.'
  , 'ذخیره تنظیمات ظاهری ناموفق بود.': 'Unable to save display settings.'
  , 'در حال دریافت تنظیمات ظاهر سایت…': 'Loading site display settings…'
  , 'file upload attachment فایل پیوست': 'file upload attachment'
  , 'عرض هر ستون باید بین ۱ و ۱۲ باشد.': 'Each column span must be between 1 and 12.'
  , 'فرم باید حداقل یک مرحله داشته باشد.': 'The form must contain at least one step.'
  , 'آدرس باید خروجی true/false برگرداند.': 'The URL must return true or false.'
  , 'یکی از عنوان‌های وضعیت پاسخ خالی است.': 'A submission status title is empty.'
  , 'button submit next previous دکمه ارسال': 'button submit next previous'
  , 'مثال: direction:ltr; border-radius:8px': 'Example: direction:ltr; border-radius:8px'
  , 'عنوان تکراری در وضعیت‌های پاسخ وجود دارد.': 'Submission status titles must be unique.'
  , 'تاریخ پایان نمی‌تواند قبل از تاریخ شروع باشد.': 'End date cannot be before start date.'
  , 'در فهرست گیرندگان پیامک شماره تکراری وجود دارد.': 'The SMS recipient list contains duplicate numbers.'
  , 'در صورت انتخاب، روی قالب رنگی آماده اولویت دارد.': 'When selected, this overrides the preset color style.'
  , '<div class="report-body"><p>اطلاعات رکورد</p></div>': '<div class="report-body"><p>Record information</p></div>'
  , 'number numeric decimal integer عدد عددی اعشاری صحیح': 'number numeric decimal integer'
  , 'زمان شرط معتبر نیست؛ زمان را از انتخابگر وارد کنید.': 'The condition time is invalid; select a time from the picker.'
  , 'پنل، btn-default، input-group-addon و ساختار کلاسیک': 'Panel, btn-default, input-group-addon, and classic structure'
  , 'Card، btn-secondary، form-check و ساختار Bootstrap 5': 'Card, btn-secondary, form-check, and Bootstrap 5 structure'
  , 'تاریخ شرط معتبر نیست؛ تاریخ را از تقویم انتخاب کنید.': 'The condition date is invalid; select a date from the calendar.'
  , '<div class="report-header"><h2>عنوان گزارش</h2></div>': '<div class="report-header"><h2>Report title</h2></div>'
  , 'اسکریپت‌ها و رویدادهای ناامن هنگام نمایش حذف می‌شوند.': 'Unsafe scripts and event handlers are removed when rendering.'
  , 'بدون نیاز به نوشتن HTML یا CSS، رنگ متن را از پالت انتخاب کنید.': 'Choose the text color from the palette without writing HTML or CSS.'
  , 'پسوند (.pdf)، MIME (image/png) و الگوی MIME (image/*) پشتیبانی می‌شود.': 'Extensions (.pdf), MIME types (image/png), and MIME patterns (image/*) are supported.'
  , 'HTML ذخیره می‌شود، اما تگ‌های اجرایی و Attributeهای خطرناک حذف می‌شوند.': 'HTML is saved, but executable tags and unsafe attributes are removed.'
  , 'با حرف انگلیسی شروع شود؛ این مقدار پس از ثبت پاسخ‌ها بهتر است تغییر نکند.': 'Start with an English letter; avoid changing this after submissions exist.'
  , 'نام فنی فرم باید با حرف انگلیسی شروع شود و فقط شامل حروف، عدد و زیرخط باشد.': 'The form technical name must start with an English letter and contain only letters, numbers, and underscores.'
  , 'در خروجی فقط فضای خالی ایجاد می‌شود و خطوط راهنمای طراح چاپ یا نمایش داده نمی‌شوند.': 'Only blank space is rendered; designer guides are neither displayed nor printed.'
  , 'خالی یعنی هر عدد اعشاری مجاز است؛ ۱ برای عدد صحیح و ۰٫۱ برای یک رقم اعشار مناسب است.': 'Blank allows any decimal; use 1 for integers or 0.1 for one decimal place.'
  , 'فقط حروف انگلیسی، عدد و زیرخط مجاز است. این نام برای داده‌های ذخیره‌شده استفاده می‌شود.': 'Only English letters, numbers, and underscores are allowed. This name is used for stored data.'
  , 'تاریخ شروع شمسی معتبر نیست؛ تاریخ را از تقویم انتخاب کنید یا با قالب 1405/01/01 وارد کنید.': 'The start date is invalid; select it from the calendar or enter it as 2026/03/21.'
  , 'تاریخ پایان شمسی معتبر نیست؛ تاریخ را از تقویم انتخاب کنید یا با قالب 1405/01/01 وارد کنید.': 'The end date is invalid; select it from the calendar or enter it as 2026/03/21.'
  , 'هر Attribute را در یک خط وارد کنید. data-* و aria-* و Attributeهای استاندارد امن قابل استفاده‌اند.': 'Enter one attribute per line. data-*, aria-*, and safe standard attributes are supported.'
,

  // Complete designer and legacy-route coverage for the standalone English host.
  'جستجوی کنترل...': 'Search controls…',
  'فهرست': 'List',
  '← فهرست': '← List',
  'لایه را اینجا قرار دهید': 'Drop the layout here',
  'لایه را در انتها قرار دهید': 'Place the layout at the end',
  'لایه را داخل پنل اینجا قرار دهید': 'Drop the layout inside the panel here',
  'لایه را در بخش ورود ردیف اینجا قرار دهید': 'Drop the layout in the row-entry area here',
  'یک لایه از ستون ابزار اضافه کنید.': 'Add a layout from the tools panel.',
  'کنترلی انتخاب نشده است': 'No control is selected.',
  'لایه‌ای انتخاب نشده است': 'No layout is selected.',
  'روی یک کنترل در بوم فرم کلیک کنید تا تنظیمات آن نمایش داده شود.': 'Select a control on the form canvas to edit its settings.',
  'روی حاشیه یک لایه کلیک کنید تا تنظیمات ستون‌ها نمایش داده شود.': 'Select a layout border to edit its columns.',
  'افزودن': 'Add',
  'انتخاب': 'Select',
  'انصراف': 'Cancel',
  'پاک کردن': 'Clear',
  'جزئیات': 'Details',
  'عملیات': 'Actions',
  'اطلاعات': 'Information',
  'بازیابی': 'Restore',
  'پیش‌فرض': 'Default',
  'از تاریخ': 'From date',
  'تا تاریخ': 'To date',
  'جدیدترین': 'Newest',
  'قدیمی‌ترین': 'Oldest',
  'حذف گروه': 'Delete group',
  'وضعیت‌ها': 'Statuses',
  'ارسال فرم': 'Submit form',
  'حروف بزرگ': 'Uppercase',
  'حروف کوچک': 'Lowercase',
  'خالی باشد': 'Is empty',
  'خالی نباشد': 'Is not empty',
  'شامل نباشد': 'Does not contain',
  'شناسه فنی': 'Technical ID',
  'عرض از ۱۲': 'Span out of 12',
  'مرحله اول': 'First step',
  'مرحله دوم': 'Second step',
  'مرحله قبل': 'Previous step',
  'مرحله بعد': 'Next step',
  'گزینه اول': 'First option',
  'افزودن شرط': 'Add condition',
  'بدون اتصال': 'No binding',
  'بدون فیلتر': 'No filter',
  'تاریخ شمسی': 'Persian date',
  'متن نمایشی': 'Display text',
  'متن چندخطی': 'Multiline text',
  'همه فرم‌ها': 'All forms',
  'ورودی عددی': 'Number input',
  'ورودی متنی': 'Text input',
  'گروه ورودی': 'Input group',
  'انتخاب فیلد': 'Select field',
  'بدون عملکرد': 'No action',
  'تقسیم مساوی': 'Distribute evenly',
  'طراحی گزارش': 'Report design',
  'فیلدهای فرم': 'Form fields',
  'زمان نوشتاری': 'Time in words',
  'تاریخ نوشتاری': 'Date in words',
  'تلفن ثابت ایران': 'Iranian landline',
  'کد ملی ایران': 'Iranian national ID',
  'آدرس اینترنتی': 'Web address',
  'اطلاعات رکورد': 'Record information',
  'بزرگ‌تر': 'Greater than',
  'کوچک‌تر': 'Less than',
  'بزرگ‌تر مساوی': 'Greater than or equal',
  'کوچک‌تر مساوی': 'Less than or equal',
  'بزرگ‌تر یا مساوی': 'Greater than or equal',
  'کوچک‌تر یا مساوی': 'Less than or equal',
  'برابر': 'Equals',
  'نابرابر': 'Not equal',
  'حذف این مرحله': 'Delete this step',
  'شرط‌های جستجو': 'Search conditions',
  'ورودی‌های فرم': 'Form inputs',
  'گزینه تیک‌دار': 'Checkbox option',
  'گزینه رادیویی': 'Radio option',
  '＋ افزودن ستون': '＋ Add column',
  '＋ افزودن گزینه': '＋ Add option',
  'شامل حروف باشد': 'Include letters',
  'نمایش کامل فرم': 'Full form preview',
  'نمونه پیام خطا': 'Error message example',
  'نمونه پیام هشدار': 'Warning message example',
  'نمونه پیام موفقیت': 'Success message example',
  'بدون اعتبارسنجی': 'No validation',
  'تصویر واکنش‌گرا': 'Responsive image',
  'تصویر واکنش‌گرا باشد': 'Use responsive image',
  'همیشه مخفی باشد': 'Always hidden',
  'گزارش پیدا نشد.': 'Report not found.',
  'افزودن گروه اصلی': 'Add root group',
  'ایجاد گزارش جدید': 'Create new report',
  'سازگاری Bootstrap': 'Bootstrap compatibility',
  'فرم‌های تکمیل‌شده': 'Completed forms',
  'مجموع عرض ستون‌ها': 'Total column span',
  'افزودن ستون نمایشی': 'Add display column',
  'بدون مقدار پیش‌فرض': 'No default value',
  'ستون‌ها و وضعیت‌ها': 'Columns and statuses',
  'جدول، پیام و پیشرفت': 'Table, message and progress',
  'بازبینی زنده کنترل‌ها': 'Live control review',
  'غیرفعال در حالت تکمیل': 'Disabled during completion',
  'ستون‌های داده‌ای جدول:': 'Table data columns:',
  'شرطی در این گروه نیست.': 'There are no conditions in this group.',
  'حذف ستون و انتقال محتوا': 'Delete column and move content',
  'سفارشی‌سازی پایدار HTML': 'Persistent HTML customization',
  'فرم مبنا را انتخاب کنید': 'Select the source form',
  'گزارش از نتایج این صفحه': 'Report from this page results',
  'امکان بازگشت از این مرحله': 'Allow returning from this step',
  'تکمیل این فیلد الزامی است': 'This field is required',
  'چیدمان این لایه کامل است.': 'This layout is complete.',
  'کنترل‌های نمایشی و ترکیبی': 'Display and composite controls',
  'برای امنیت اعمال نمی‌شوند.': 'They are not applied for security reasons.',
  'توقف آنی پس از تکمیل ظرفیت': 'Stop immediately when capacity is reached',
  'کنترلی با این عبارت پیدا نشد.': 'No control matched this search.',
  'پاسخی با این فیلترها پیدا نشد.': 'No submission matched these filters.',
  'به‌صورت پیش‌فرض انتخاب شده باشد': 'Selected by default',
  'لایه‌ها و محتوای بخش ورود ردیف:': 'Row-entry layouts and content:',
  'پیش‌نمایش اطلاعات پس از این مرحله': 'Preview data after this step',
  'رکوردی برای گزارش انتخاب نشده است.': 'No record was selected for the report.',
  'نمونه متن HTML و توضیحات داخل فرم.': 'Sample HTML text and form description.',
  'شرط‌ها یا بازه تاریخ را تغییر دهید.': 'Change the conditions or date range.',
  'مقدار در پاسخ‌های این فرم یکتا باشد': 'Value must be unique in this form submissions',
  'تغییر انتخاب‌شده هنوز ذخیره نشده است.': 'The selected change has not been saved yet.',
  'نمایش کنترل وابسته به مقدار فیلد دیگری باشد': 'Display this control based on another field value',
  'عبارت فیلد در محل نشانگر ویرایشگر درج می‌شود.': 'The field expression is inserted at the editor cursor.',
  'جستجو، فیلتر، گزارش، وضعیت و مدیریت پاسخ‌های کاربران': 'Search, filter, report, status and manage user submissions',
  'طراحی قالب‌های چاپ و نمایش اطلاعات فرم‌های تکمیل‌شده': 'Design print templates and completed-form views',
  'جعبه متنی، انتخابی، رادیو، چک‌باکس یا آپلود فایل را اینجا رها کنید': 'Drop a text box, dropdown, radio, checkbox or file upload here',
  'لایه، متن، کد امنیتی، تصویر، فاصله، دکمه یا برچسب متصل را اینجا رها کنید': 'Drop a layout, text, security code, image, spacer, button or bound label here',
  'این کنترل همیشه نمایش داده می‌شود؛ مگر اینکه گزینه «همیشه مخفی» فعال باشد.': 'This control is always shown unless “Always hidden” is enabled.',
  'شرط‌ها به‌صورت داده ساخت‌یافته ذخیره می‌شوند و دیگر عبارت خام AngularJS تولید نمی‌شود.': 'Conditions are stored as structured data; raw AngularJS expressions are no longer generated.',
  'این بخش جایگزین تغییر مستقیم DOM با Inspect است. تغییرات همراه فرم ذخیره می‌شوند. Attributeهای رویدادی مانند': 'This replaces direct DOM editing. Changes are stored with the form. Event attributes such as',
  'نسخه Bootstrap قالب این سایت را انتخاب کنید تا داشبورد، فرم عمومی، گزارش‌ها و همه کنترل‌ها با همان نسل هماهنگ شوند.': 'Select the site theme Bootstrap version so the dashboard, public forms, reports and controls stay compatible.',
  'عرض هر ردیف در Bootstrap برابر ۱۲ واحد است. مجموع کمتر از ۱۲ فضای خالی ایجاد می‌کند و مجموع بیشتر از ۱۲ باعث شکستن ستون‌ها به خط بعد می‌شود.': 'Each Bootstrap row has 12 units. Totals below 12 leave space; totals above 12 wrap columns to the next line.',
  'چپ': 'Left',
  'راست': 'Right',
  'کوچک': 'Small',
  'متوسط': 'Medium',
  'بزرگ': 'Large',
  'اصلی': 'Primary',
  'خنثی': 'Default',
  'موفق': 'Success',
  'خطر': 'Danger',
  'موبایل': 'Mobile',
  'ایمیل': 'Email',
  'رمز': 'Password',
  'عددی': 'Numeric',
  'چاپ': 'Print',
  'عنوان مدیریتی کنترل': 'Management label',
  'فقط در فرم‌ساز، فهرست شرط‌ها و گزارش‌ها استفاده می‌شود و در فرم خروجی نمایش داده نمی‌شود.': 'Used only in the designer, rule lists, and reports; it is not rendered in the public form.',
  'رنگ مرحله': 'Step color',
  'رنگ متن مرحله': 'Step text color',
  'لایه یا کنترل فاصله را اینجا قرار دهید': 'Drop a layout or spacer here',
  'برای انتقال لایه یا کنترل فاصله به انتهای مرحله، آن را اینجا رها کنید.': 'Drop the layout or spacer here to move it to the end of the step.',
  'با فعال‌سازی این گزینه، تمام فیلدهای داده‌ای داخل پنل نیز الزامی می‌شوند.': 'When enabled, every data-entry field inside the panel becomes required.',

  'اجازه مشاهده داشبورد را ندارید.': 'You are not allowed to view the dashboard.',
  'ایجاد فرم برای این پورتال مجاز نیست یا ظرفیت فرم تکمیل شده است.': 'Creating a form is not allowed for this portal, or its form capacity has been reached.',
  'طراحی و ویرایش فرم برای این پورتال فقط توسط کاربر ارشد DNN مجاز است.': 'Only a DNN SuperUser may design or edit forms for this portal.',
  'اجازه مشاهده پاسخ‌ها را ندارید.': 'You are not allowed to view submissions.',
  'شناسه فرم یا پاسخ معتبر نیست.': 'The form or submission identifier is invalid.',
  'بازگشت به پاسخ‌ها': 'Back to submissions',
  'اجازه مشاهده گزارش‌ها را ندارید.': 'You are not allowed to view reports.',
  'این بخش فقط برای کاربر ارشد DNN است.': 'This section is available only to DNN SuperUsers.',
  'اجازه مدیریت تنظیمات پیامک را ندارید.': 'You are not allowed to manage SMS settings.',
  'اجازه مدیریت سطوح دسترسی را ندارید.': 'You are not allowed to manage access control.',
};

const FRENCH_COPY: Record<string, string> = {
  'و': 'et',
  'یا': 'ou',
  'بستن': 'Fermer',
  'حذف': 'Supprimer',
  'ویرایش': 'Modifier',
  'تکثیر': 'Dupliquer',
  'ذخیره': 'Enregistrer',
  'ذخیره تغییرات': 'Enregistrer les modifications',
  'در حال ذخیره…': 'Enregistrement…',
  'در حال حذف…': 'Suppression…',
  'در حال انجام…': 'Traitement…',
  'در حال تکثیر…': 'Duplication…',
  'در حال بارگذاری…': 'Chargement…',
  'در حال دریافت…': 'Chargement…',
  'در حال دریافت فرم…': 'Chargement du formulaire…',
  'بازخوانی': 'Actualiser',
  'بازگشت': 'Retour',
  'بازگشت به فهرست فرم‌ها': 'Retour aux formulaires',
  'بازگشت به سایت': 'Retour au site',
  'پیش‌نمایش': 'Aperçu',
  'پیش‌نمایش فرم': 'Aperçu du formulaire',
  'بررسی': 'Vérifier',
  'بررسی ساختار فرم': 'Vérifier la structure du formulaire',
  'تاریخچه تغییرات': 'Historique des modifications',
  'بازگردانی (Ctrl+Z)': 'Annuler (Ctrl+Z)',
  'انجام مجدد (Ctrl+Y)': 'Rétablir (Ctrl+Y)',
  'ذخیره (Ctrl+S)': 'Enregistrer (Ctrl+S)',
  'ذخیره‌شده': 'Enregistré',
  'ذخیره‌نشده': 'Non enregistré',
  'فعال': 'Actif',
  'غیرفعال': 'Inactif',
  'خطا': 'Erreur',
  'هشدار': 'Avertissement',
  'تنظیمات': 'Paramètres',
  'ابزارها': 'Outils',
  'فرم': 'Formulaire',
  'عنصر': 'Élément',
  'صفحه': 'Page',
  'صفحه اول': 'Première page',
  'صفحه قبل': 'Page précédente',
  'صفحه بعد': 'Page suivante',
  'صفحه آخر': 'Dernière page',
  'انتخاب کنید': 'Sélectionner…',
  'نتیجه‌ای یافت نشد.': 'Aucun résultat trouvé.',
  'خطای ناشناخته': 'Erreur inconnue',
  'فرم جدید': 'Nouveau formulaire',
  'طراحی فرم': 'Conception du formulaire',
  'طراحی فرم جدید': 'Créer un nouveau formulaire',
  'ویرایش فرم': 'Modifier le formulaire',
  'فرم‌ساز': 'Concepteur de formulaire',
  'ابزارهای طراحی فرم': 'Outils de conception de formulaire',
  'عرض پیش‌نمایش': 'Largeur de l’aperçu',
  'همه تغییرات ذخیره شده است': 'Toutes les modifications sont enregistrées',
  'تغییرات ذخیره‌نشده وجود دارد': 'Des modifications ne sont pas enregistrées',
  'ساختار فرم بررسی شد و مشکل شناخته‌شده‌ای پیدا نشد.': 'La structure du formulaire est valide et aucun problème connu n’a été détecté.',
  'برای رفتن به محل هر مورد روی آن کلیک کنید.': 'Sélectionnez un élément pour accéder à son emplacement.',
  'بستن پیش‌نمایش': 'Fermer l’aperçu',
  'فرم بدون عنوان': 'Formulaire sans titre',
  'جستجوی کنترل…': 'Rechercher des contrôles…',
  'جستجوی کنترل': 'Rechercher des contrôles',
  'پاک‌کردن جستجو': 'Effacer la recherche',
  'لایه‌ها': 'Mises en page',
  'درخت لایه‌ها': 'Arborescence des mises en page',
  'کنترل‌های فرم': 'Contrôles du formulaire',
  'کنترل‌های عمومی': 'Contrôles généraux',
  'کنترل را اینجا رها کنید': 'Déposez un contrôle ici',
  'رها کنید': 'Déposez ici',
  'لایه را داخل پنل رها کنید': 'Déposez la mise en page dans le panneau',
  'لایه را در انتهای پنل قرار دهید': 'Placez la mise en page à la fin du panneau',
  'لایه یا کنترل نمایشی را اینجا رها کنید': 'Déposez une mise en page ou un contrôle d’affichage ici',
  'لایه را در انتهای بخش ورود ردیف قرار دهید': 'Placez la mise en page à la fin de la zone de saisie de ligne',
  'انتقال کنترل به انتهای ستون': 'Déplacer le contrôle à la fin de la colonne',
  'برای جابه‌جایی بکشید': 'Faites glisser pour déplacer',
  'با کلیک اضافه کنید یا به محل دلخواه بکشید.': 'Cliquez pour ajouter ou faites glisser vers l’emplacement souhaité.',
  'کنترل یا لایه را اینجا رها کنید': 'Déposez un contrôle ou une mise en page ici',
  'برای انتقال لایه به انتهای مرحله، آن را اینجا رها کنید.': 'Déposez la mise en page ici pour la déplacer à la fin de l’étape.',
  'جابه‌جایی کنترل': 'Déplacer le contrôle',
  'جابه‌جایی لایه': 'Déplacer la mise en page',
  'تکثیر کنترل': 'Dupliquer le contrôle',
  'تکثیر لایه': 'Dupliquer la mise en page',
  'حذف کنترل': 'Supprimer le contrôle',
  'حذف لایه': 'Supprimer la mise en page',
  '۱ ستون': '1 colonne',
  '۲ ستون': '2 colonnes',
  '۳ ستون': '3 colonnes',
  '۴ ستون': '4 colonnes',
  '1 ستون': '1 colonne',
  '2 ستون': '2 colonnes',
  '3 ستون': '3 colonnes',
  '4 ستون': '4 colonnes',
  'جعبه متنی': 'Champ texte',
  'ناحیه متنی': 'Zone de texte',
  'جعبه کشویی': 'Liste déroulante',
  'چک‌باکس': 'Case à cocher',
  'رادیوباکس': 'Boutons radio',
  'فیلد عددی': 'Champ numérique',
  'آپلود فایل': 'Téléversement de fichier',
  'جدول اطلاعاتی': 'Tableau de données',
  'پنل': 'Panneau',
  'تصویر': 'Image',
  'متن و HTML': 'Texte et HTML',
  'فاصله': 'Espacement',
  'فاصله نمایشی': 'Espacement',
  'دکمه': 'Bouton',
  'کد امنیتی': 'Code de sécurité',
  'برچسب متصل': 'Libellé lié',
  'تاریخ و زمان': 'Date et heure',
  'انتخابگر تاریخ و زمان': 'Sélecteur de date et d’heure',
  'پیشرفت نمونه': 'Exemple de progression',
  'تنظیمات فرم': 'Paramètres du formulaire',
  'عنوان فرم': 'Titre du formulaire',
  'نام فنی': 'Nom technique',
  'نام فنی فرم': 'Nom technique du formulaire',
  'توضیحات': 'Description',
  'فرم فعال باشد': 'Formulaire actif',
  'بدون محدودیت زمانی': 'Sans limite de temps',
  'تاریخ شروع': 'Date de début',
  'تاریخ پایان': 'Date de fin',
  'پیام عدم دسترسی/انقضا': 'Message d’indisponibilité ou d’expiration',
  'ظرفیت محدود': 'Capacité limitée',
  'ظرفیت': 'Capacité',
  'پیام ظرفیت': 'Message de capacité',
  'نمایش پیام نهایی': 'Afficher le message final',
  'نمایش کد پیگیری': 'Afficher le code de suivi',
  'گیرندگان پیامک': 'Destinataires SMS',
  'مرحله‌ها': 'Étapes',
  'مراحل فرم': 'Étapes du formulaire',
  'افزودن مرحله': 'Ajouter une étape',
  'عنوان مرحله': 'Titre de l’étape',
  'نام فنی مرحله': 'Nom technique de l’étape',
  'امکان بازگشت': 'Autoriser le retour',
  'پیش‌نمایش قبل از ادامه': 'Aperçu avant de continuer',
  'وضعیت‌های پاسخ': 'Statuts des réponses',
  'افزودن وضعیت': 'Ajouter un statut',
  'عنوان وضعیت': 'Titre du statut',
  'گیرنده': 'Destinataire',
  'تنظیمات نمایش سربرگ فرم': 'Paramètres d’affichage de l’en-tête du formulaire',
  'تراز عنوان': 'Alignement du titre',
  'پیش‌فرض قالب سایت': 'Valeur par défaut du thème du site',
  'پیش‌فرض زبان': 'Selon la langue',
  'رنگ عنوان': 'Couleur du titre',
  'رنگ عنوان؛ انتخاب رنگ': 'Couleur du titre ; choisir une couleur',
  'نمایش کامل بخش سربرگ فرم': 'Afficher l’intégralité de l’en-tête du formulaire',
  'رنگ پس‌زمینه سربرگ': 'Couleur d’arrière-plan de l’en-tête',
  'رنگ پس‌زمینه سربرگ؛ انتخاب رنگ': 'Couleur d’arrière-plan de l’en-tête ; choisir une couleur',
  'پیش‌نمایش عنوان فرم': 'Aperçu du titre du formulaire',
  'نمایش بازه زمانی در سربرگ فرم': 'Afficher la période d’activité dans l’en-tête du formulaire',
  'مخفی‌کردن کامل سربرگ فقط عنوان، توضیحات، بازه زمانی و وضعیت ظرفیت را از صفحه عمومی حذف می‌کند؛ محدودیت‌های تاریخ و ظرفیت همچنان در زمان ثبت اجرا می‌شوند.': 'Masquer entièrement l’en-tête retire uniquement le titre, la description, la période d’activité et l’état de capacité de la page publique ; les règles de date et de capacité restent appliquées lors de l’envoi.',
  'بازنشانی ستون‌ها': 'Réinitialiser les colonnes',
  'ستون‌ها بازنشانی شدند؛ برای مشاهده نتایج، اعمال فیلتر را بزنید.': 'Les colonnes ont été réinitialisées. Appliquez le filtre pour actualiser les résultats.',
  'فیلدهای فرم هنوز برای فیلتر و ستون‌ها آماده نیستند.': 'Les champs du formulaire ne sont pas encore prêts pour les filtres et les colonnes.',
  'ابتدا منتظر دریافت فیلدهای فرم بمانید.': 'Attendez le chargement des champs du formulaire.',
  'تعریف فرم برای فیلتر و ستون‌ها دریافت نشد:': 'Impossible de charger la définition du formulaire pour les filtres et les colonnes :',
  'تنظیمات کنترل': 'Paramètres du contrôle',
  'ویژگی‌ها': 'Propriétés',
  'اعتبارسنجی': 'Validation',
  'شرط نمایش': 'Règle de visibilité',
  'پیشرفته': 'Avancé',
  'عنوان کنترل': 'Libellé du contrôle',
  'نام فنی مدل': 'Nom du modèle',
  'نام فنی مدل *': 'Nom du modèle *',
  'کلید فنی داده': 'Clé de données',
  'کلید فنی فرم': 'Clé du formulaire',
  'کپی': 'Copier',
  'نمایش': 'Afficher',
  'تنظیمات پیشرفته فرم': 'Paramètres avancés du formulaire',
  'کلید فنی جدید': 'Nouvelle clé de données',
  'کلید فنی جدید فرم': 'Nouvelle clé du formulaire',
  'فعال‌کردن ویرایش تخصصی کلید فنی': 'Activer la modification experte de la clé de données',
  'فعال‌کردن ویرایش تخصصی کلید فرم': 'Activer la modification experte de la clé du formulaire',
  'اعمال کلید جدید': 'Appliquer la nouvelle clé',
  'شناسه داخلی این فیلد برای ذخیره پاسخ، گزارش، فیلتر، شرط نمایش و اتصال کنترل‌ها است.': 'Cet identifiant interne du champ est utilisé pour les réponses enregistrées, les rapports, les filtres, les règles de visibilité et les liaisons de contrôles.',
  'شناسه داخلی فرم و پیشوند مسیرهای داده، فیلترها و گزارش‌ها است. پس از اولین ذخیره نباید تغییر کند.': 'Il s’agit de l’identifiant interne du formulaire et du préfixe des chemins de données, filtres et rapports. Il ne doit plus être modifié après le premier enregistrement.',
  'این فرم قبلاً ذخیره شده است. تغییر کلید فنی از این صفحه غیرفعال است و فقط باید همراه Migration داده انجام شود.': 'Ce formulaire a déjà été enregistré. La modification de la clé de données est désactivée sur cette page et doit uniquement être effectuée avec une migration des données.',
  'این فرم قبلاً ذخیره شده است. تغییر کلید فنی فرم از این صفحه غیرفعال است و فقط باید همراه Migration داده انجام شود.': 'Ce formulaire a déjà été enregistré. La modification de la clé du formulaire est désactivée sur cette page et doit uniquement être effectuée avec une migration des données.',
  'پس از ساخت شرط، اتصال یا گزارش، تغییر این مقدار می‌تواند وابستگی‌ها را نامعتبر کند.': 'Modifier cette valeur après la création de règles, de liaisons ou de rapports peut invalider leurs dépendances.',
  'پس از ذخیره فرم یا ساخت فیلتر و گزارش، تغییر این مقدار می‌تواند وابستگی‌ها را نامعتبر کند.': 'Modifier cette valeur après l’enregistrement du formulaire ou la création de filtres et de rapports peut invalider leurs dépendances.',
  'کلید باید با حرف انگلیسی شروع شود و فقط شامل حروف، عدد و زیرخط باشد.': 'La clé doit commencer par une lettre anglaise et contenir uniquement des lettres, des chiffres et des traits de soulignement.',
  'کلید فرم باید با حرف انگلیسی شروع شود و فقط شامل حروف، عدد و زیرخط باشد.': 'La clé du formulaire doit commencer par une lettre anglaise et contenir uniquement des lettres, des chiffres et des traits de soulignement.',
  'این کلید در محدوده همین مرحله یا جدول تکراری است.': 'Cette clé est déjà utilisée dans la même étape ou le même tableau.',
  'ID کنترل': 'ID du contrôle',
  'متن راهنما (Placeholder)': 'Texte indicatif (placeholder)',
  'نوع ورودی': 'Type de saisie',
  'مقدار پیش‌فرض': 'Valeur par défaut',
  'مقدار نامعتبر': 'Valeur non valide',
  'اندازه': 'Taille',
  'اندازه متن': 'Taille du texte',
  'چینش': 'Alignement',
  'تراز متن': 'Alignement du texte',
  'نوع نمایش': 'Mode d’affichage',
  'عرض کامل': 'Pleine largeur',
  'درون‌خطی': 'En ligne',
  'راست‌چین': 'À droite',
  'وسط': 'Centré',
  'وسط‌چین': 'Centré',
  'چپ‌چین': 'À gauche',
  'تراز دوطرفه': 'Justifié',
  'عادی': 'Normal',
  'درشت و برجسته': 'Grand et gras',
  'رنگ': 'Couleur',
  'رنگ متن': 'Couleur du texte',
  'رنگ سربرگ پنل': 'Couleur de l’en-tête du panneau',
  'رنگ متن سربرگ': 'Couleur du texte de l’en-tête',
  'رنگ زمینه بدنه پنل': 'Arrière-plan du corps du panneau',
  'رنگ کادر': 'Couleur de la bordure',
  'قالب رنگی آماده': 'Style de couleur prédéfini',
  'بدون مدل': 'Aucun modèle',
  'فیلد مبنا': 'Champ source',
  'فیلد متصل': 'Champ lié',
  'مقدار مقایسه': 'Valeur de comparaison',
  'عملگر': 'Opérateur',
  'عنوان': 'Titre',
  'متن دکمه': 'Texte du bouton',
  'عملکرد': 'Action',
  'محتوای HTML': 'Contenu HTML',
  'آدرس تصویر': 'URL de l’image',
  'متن جایگزین': 'Texte alternatif',
  'عرض (px)': 'Largeur (px)',
  'ارتفاع (px)': 'Hauteur (px)',
  'ارتفاع فاصله (پیکسل)': 'Hauteur de l’espacement (pixels)',
  'فیلتر نمایش': 'Filtre d’affichage',
  'پیشوند': 'Préfixe',
  'پسوند': 'Suffixe',
  'Style کنترل': 'Style du contrôle',
  'کلاس‌های CSS کنترل': 'Classes CSS du contrôle',
  'Attributeهای کنترل': 'Attributs du contrôle',
  'کلاس‌های CSS Wrapper': 'Classes CSS du conteneur',
  'Attributeهای Wrapper': 'Attributs du conteneur',
  'HTML قبل از کنترل': 'HTML avant le contrôle',
  'HTML بعد از کنترل': 'HTML après le contrôle',
  'CSS class عمومی (Legacy)': 'Classe CSS globale (héritée)',
  'نمایشگرها': 'Paramètres d’affichage',
  'گزینه‌ها': 'Options',
  'افزودن گزینه': 'Ajouter une option',
  'متن گزینه اولیه': 'Texte de l’option initiale',
  'گزینه غیرفعال': 'Option désactivée',
  'فیلد الزامی': 'Champ obligatoire',
  'پیام فیلد الزامی': 'Message de champ obligatoire',
  'حداقل طول': 'Longueur minimale',
  'حداکثر طول': 'Longueur maximale',
  'پیام حداقل طول': 'Message de longueur minimale',
  'پیام حداکثر طول': 'Message de longueur maximale',
  'حداقل مقدار': 'Valeur minimale',
  'حداکثر مقدار': 'Valeur maximale',
  'پیام کمتر از حداقل': 'Message de valeur inférieure au minimum',
  'پیام بیشتر از حداکثر': 'Message de valeur supérieure au maximum',
  'گام عددی (Step)': 'Pas numérique',
  'پیام گام نامعتبر': 'Message de pas non valide',
  'عبارت منظم (Regex)': 'Expression régulière (Regex)',
  'پیام Regex': 'Message Regex',
  'پیام Mask': 'Message de masque',
  'آدرس اعتبارسنجی Remote': 'URL de validation distante',
  'پیام Remote': 'Message de validation distante',
  'مقدار یکتا باشد': 'La valeur doit être unique',
  'پیام مقدار تکراری': 'Message de valeur en double',
  'نوع/پسوندهای مجاز': 'Types ou extensions autorisés',
  'پیام نوع فایل نامعتبر': 'Message de type de fichier non valide',
  'حداقل حجم (KB)': 'Taille minimale (Ko)',
  'حداکثر حجم (KB)': 'Taille maximale (Ko)',
  'پیام حداقل حجم': 'Message de taille minimale',
  'پیام حداکثر حجم': 'Message de taille maximale',
  'حداقل ردیف': 'Nombre minimal de lignes',
  'پیام حداقل ردیف': 'Message de nombre minimal de lignes',
  'طول کد': 'Longueur du code',
  'شامل حرف باشد': 'Inclure des lettres',
  'کلید عمومی reCAPTCHA': 'Clé publique reCAPTCHA',
  'تنظیمات لایه': 'Paramètres de mise en page',
  'ستون‌ها': 'Colonnes',
  'عرض ستون': 'Largeur de colonne',
  'مجموع عرض': 'Largeur totale',
  'توزیع مساوی': 'Répartir équitablement',
  'مجموع عرض ستون‌ها باید ۱۲ باشد.': 'La somme des largeurs de colonnes doit être égale à 12.',
  'فضای خالی': 'Espace inutilisé',
  'پاسخ‌های ثبت‌شده': 'Réponses enregistrées',
  'پاسخ ثبت‌شده': 'Réponse enregistrée',
  'انتخاب فرم': 'Sélectionner un formulaire',
  'فقط دارای پاسخ': 'Uniquement avec des réponses',
  'فقط فرم‌های فعال': 'Uniquement les formulaires actifs',
  'فقط منقضی‌نشده': 'Uniquement les formulaires non expirés',
  'اعمال فیلتر': 'Appliquer les filtres',
  'پاک کردن فیلترها': 'Effacer les filtres',
  'پاک‌کردن فیلترها': 'Effacer les filtres',
  'فیلترها': 'Filtres',
  'پرس‌وجوهای ذخیره‌شده': 'Requêtes enregistrées',
  'نام پرس‌وجو': 'Nom de la requête',
  'ذخیره پرس‌وجو': 'Enregistrer la requête',
  'مرتب‌سازی': 'Trier par',
  'جهت مرتب‌سازی': 'Ordre de tri',
  'صعودی': 'Croissant',
  'نزولی': 'Décroissant',
  'تعداد در صفحه': 'Éléments par page',
  'جستجوی عنوان': 'Rechercher dans le titre',
  'اعمال': 'Appliquer',
  'خروجی Excel': 'Exporter vers Excel',
  'تغییر وضعیت': 'Modifier le statut',
  'تغییر وضعیت پاسخ': 'Modifier le statut de la réponse',
  'وضعیت': 'Statut',
  'یادداشت وضعیت': 'Note de statut',
  'ستون‌های سیستمی': 'Colonnes système',
  'کد ثبت': 'Code de réponse',
  'تاریخ ثبت': 'Date de réponse',
  'تاریخ': 'Date',
  'نتایج': 'Résultats',
  'ستون انتخابی از فیلدهای فرم وجود ندارد.': 'Aucune colonne issue des champs du formulaire n’est sélectionnée.',
  'گزارش‌ساز': 'Concepteur de rapports',
  'گزارش‌های سفارشی': 'Rapports personnalisés',
  'گزارش جدید': 'Nouveau rapport',
  'ایجاد گزارش': 'Créer un rapport',
  'ویرایش گزارش': 'Modifier le rapport',
  'ذخیره گزارش': 'Enregistrer le rapport',
  'انتخاب گزارش': 'Sélectionner un rapport',
  'عنوان گزارش': 'Titre du rapport',
  'قالب گزارش': 'Modèle du rapport',
  'سربرگ': 'En-tête',
  'بدنه': 'Corps',
  'پابرگ': 'Pied de page',
  'اتصال به فایل فرم': 'Lier à un champ du formulaire',
  'مدیریت گزارش': 'Gestion des rapports',
  'طراحی قالب و گزارش': 'Concevoir des modèles et des rapports',
  'تنظیمات نمایش': 'Paramètres d’affichage',
  'انتخاب نسخه Bootstrap': 'Sélectionner la version de Bootstrap',
  'ذخیره برای این سایت': 'Enregistrer pour ce site',
  'قالب‌های قدیمی DNN': 'Thèmes DNN hérités',
  'قالب‌های جدید و مدرن': 'Thèmes modernes',
  'فرم ایجاد شد.': 'Formulaire créé.',
  'فرم حذف شد.': 'Formulaire supprimé.',
  'فرم تکثیر شد.': 'Formulaire dupliqué.',
  'تغییرات فرم ذخیره شد.': 'Modifications du formulaire enregistrées.',
  'سرور ذخیره فرم را تأیید نکرد.': 'Le serveur n’a pas confirmé l’enregistrement du formulaire.',
  'خطا در ذخیره فرم': 'Impossible d’enregistrer le formulaire',
  'حذف فرم ناموفق بود.': 'Impossible de supprimer le formulaire.',
  'گزارش ذخیره شد.': 'Rapport enregistré.',
  'گزارش حذف شد.': 'Rapport supprimé.',
  'خطا در ذخیره گزارش': 'Impossible d’enregistrer le rapport',
  'خطا در حذف گزارش': 'Impossible de supprimer le rapport',
  'پاسخ حذف شد.': 'Réponse supprimée.',
  'خطا در حذف پاسخ': 'Impossible de supprimer la réponse',
  'وضعیت ذخیره شد.': 'Statut enregistré.',
  'خطا در ذخیره وضعیت': 'Impossible d’enregistrer le statut',
  'خطا در دریافت وضعیت': 'Impossible de charger le statut',
  'پرس‌وجو ذخیره شد.': 'Requête enregistrée.',
  'پرس‌وجو حذف شد.': 'Requête supprimée.',
  'خطا در ذخیره پرس‌وجو': 'Impossible d’enregistrer la requête',
  'خطا در حذف پرس‌وجو': 'Impossible de supprimer la requête',
  'ابتدا یک فرم انتخاب کنید.': 'Sélectionnez d’abord un formulaire.',
  'عنوان گزارش را وارد کنید.': 'Saisissez un titre de rapport.',
  'نام پرس‌وجو را وارد کنید.': 'Saisissez un nom de requête.',
  'تاریخ شروع بازه معتبر نیست.': 'La date de début de la période n’est pas valide.',
  'تاریخ پایان بازه معتبر نیست.': 'La date de fin de la période n’est pas valide.',
  'تاریخ پایان بازه نمی‌تواند قبل از تاریخ شروع باشد.': 'La date de fin de la période ne peut pas précéder la date de début.',
  'تغییرات ذخیره‌نشده از بین برود؟': 'Abandonner les modifications non enregistrées ?',
  'فرم و اطلاعات طراحی آن حذف شود؟': 'Supprimer le formulaire et ses données de conception ?',
  'این پاسخ حذف شود؟': 'Supprimer cette réponse ?',
  'این گزارش حذف شود؟': 'Supprimer ce rapport ?',
  'گزارش حذف شود؟': 'Supprimer le rapport ?',
  'این مرحله حذف شود؟': 'Supprimer cette étape ?',
  'پرس‌وجوی ذخیره‌شده حذف شود؟': 'Supprimer la requête enregistrée ?',
  'فرم جدیدی را از صفر طراحی کنید.': 'Créez un nouveau formulaire à partir de zéro.',
  'فرمی با این فیلترها پیدا نشد.': 'Aucun formulaire ne correspond à ces filtres.',
  'فیلترها را پاک کنید یا یک فرم جدید بسازید.': 'Effacez les filtres ou créez un nouveau formulaire.',
  'توضیحی برای این فرم ثبت نشده است.': 'Aucune description n’a été saisie pour ce formulaire.',
  'جستجو در عنوان فرم‌ها…': 'Rechercher dans les titres de formulaires…',
  'فرم‌ها را جستجو، مدیریت و برای دریافت پاسخ آماده کنید.': 'Recherchez, gérez et préparez les formulaires à recevoir des réponses.',
  'فرم‌های شما در یک نگاه': 'Tous vos formulaires en un coup d’œil',
  'فرم‌های نمایش‌داده‌شده': 'Formulaires affichés',
  'داشبورد مدیریتی': 'Tableau de bord de gestion',
  'مدیریت هوشمند فرم‌ها': 'Gestion intelligente des formulaires',
  'نمای کلی و مدیریت فرم‌ها': 'Vue d’ensemble et gestion des formulaires',
  'ساخت و ویرایش فرم': 'Créer et modifier des formulaires',
  'فیلتر، وضعیت و خروجی': 'Filtres, statuts et exportation',
  'ظاهر و سازگاری فرم‌ها': 'Apparence et compatibilité des formulaires',
  'خروج از محیط مدیریت': 'Quitter l’administration',
  'زبان': 'Langue',
  'شماره': 'Numéro',
  'شناسه': 'ID',
  'فارسی': 'Persan',
  'مقدار': 'Valeur',
  '۱۲۳٫۵': '123,5',
  'در صفحه': 'par page',
  'نامحدود': 'Illimité',
  'فرم فعال': 'Formulaire actif',
  'بستن پیام': 'Fermer le message',
  'تعداد ثبت': 'Nombre de réponses',
  'صفحه‌بندی': 'Pagination',
  'منوی اصلی': 'Menu principal',
  ' (زیرگروه)': ' (sous-groupe)',
  'آپلود تصویر': 'Téléverser une image',
  'بازه فعالیت': 'Période d’activité',
  'تاریخ ایجاد': 'Date de création',
  'مدیریت داده': 'Gestion des données',
  'مرکز مدیریت': 'Centre d’administration',
  'تولید کد جدید': 'Générer un nouveau code',
  'ساخت فرم تازه': 'Créer un nouveau formulaire',
  'نمونه توضیحات': 'Exemple de description',
  'نمونه دکمه‌ها': 'Exemples de boutons',
  'ایجاد فرم جدید': 'Créer un nouveau formulaire',
  'داشبورد فرم‌ها': 'Tableau de bord des formulaires',
  'تصویر آپلود شد.': 'Image téléversée.',
  'اعتبارسنجی آماده': 'Validation intégrée',
  'بستن منوی مدیریت': 'Fermer le menu d’administration',
  'خطا در تکثیر فرم': 'Impossible de dupliquer le formulaire',
  'طراحی گزارش جدید': 'Créer un nouveau rapport',
  'نمونه ورودی متنی': 'Exemple de champ texte',
  'image picture عکس': 'image illustration photo',
  'دارای ظرفیت محدود': 'Capacité limitée',
  'radio option گزینه': 'option radio',
  'خطا در آپلود تصویر': 'Impossible de téléverser l’image',
  'باز کردن منوی مدیریت': 'Ouvrir le menu d’administration',
  'جمع کردن منوی مدیریت': 'Réduire le menu d’administration',
  'check boolean بله خیر': 'case à cocher booléenne oui non',
  'recaptcha google کپچا': 'reCAPTCHA Google captcha',
  'space separator فاصله': 'espace séparateur espacement',
  'عنوان فرم الزامی است.': 'Le titre du formulaire est obligatoire.',
  'نمونه تصویر واکنش‌گرا': 'Exemple d’image adaptative',
  'پیام اعتبارسنجی آماده': 'Message de validation intégré',
  'captcha security امنیتی': 'captcha code de sécurité',
  'خطا در بارگذاری پرس‌وجو': 'Impossible de charger la requête',
  'نام فنی فرم الزامی است.': 'Le nom technique du formulaire est obligatoire.',
  'table grid row جدول ردیف': 'tableau grille ligne',
  'date time clock تاریخ زمان': 'date heure horloge',
  'panel group container گروه': 'panneau groupe conteneur',
  'select dropdown list انتخاب': 'sélection liste déroulante',
  'input textbox text متن ورودی': 'champ texte saisie',
  'label bind value برچسب مقدار': 'libellé liaison valeur',
  'paragraph html rich text متن': 'paragraphe HTML texte enrichi',
  'ظرفیت فرم باید حداقل ۱ باشد.': 'La capacité du formulaire doit être au moins égale à 1.',
  'یک لایه بدون ستون وجود دارد.': 'Une mise en page ne contient aucune colonne.',
  'textarea multiline متن چندخطی': 'zone de texte multiligne',
  'فیلد و مقدار شرط را مشخص کنید.': 'Sélectionnez le champ source et la valeur de la condition.',
  'نام فنی یکی از مراحل خالی است.': 'Le nom technique d’une étape est vide.',
  'ذخیره تنظیمات ظاهری ناموفق بود.': 'Impossible d’enregistrer les paramètres d’affichage.',
  'در حال دریافت تنظیمات ظاهر سایت…': 'Chargement des paramètres d’affichage du site…',
  'file upload attachment فایل پیوست': 'téléversement de fichier pièce jointe',
  'عرض هر ستون باید بین ۱ و ۱۲ باشد.': 'La largeur de chaque colonne doit être comprise entre 1 et 12.',
  'فرم باید حداقل یک مرحله داشته باشد.': 'Le formulaire doit contenir au moins une étape.',
  'آدرس باید خروجی true/false برگرداند.': 'L’URL doit renvoyer true ou false.',
  'یکی از عنوان‌های وضعیت پاسخ خالی است.': 'Le titre d’un statut de réponse est vide.',
  'button submit next previous دکمه ارسال': 'bouton envoyer suivant précédent',
  'مثال: direction:ltr; border-radius:8px': 'Exemple : direction:ltr; border-radius:8px',
  'عنوان تکراری در وضعیت‌های پاسخ وجود دارد.': 'Les titres des statuts de réponse doivent être uniques.',
  'تاریخ پایان نمی‌تواند قبل از تاریخ شروع باشد.': 'La date de fin ne peut pas précéder la date de début.',
  'در فهرست گیرندگان پیامک شماره تکراری وجود دارد.': 'La liste des destinataires SMS contient des numéros en double.',
  'در صورت انتخاب، روی قالب رنگی آماده اولویت دارد.': 'Lorsqu’elle est sélectionnée, cette valeur remplace le style de couleur prédéfini.',
  '<div class="report-body"><p>اطلاعات رکورد</p></div>': '"<div class=""report-body""><p>Informations de la réponse</p></div>"',
  'number numeric decimal integer عدد عددی اعشاری صحیح': 'nombre numérique décimal entier',
  'زمان شرط معتبر نیست؛ زمان را از انتخابگر وارد کنید.': 'L’heure de la condition n’est pas valide ; sélectionnez une heure dans le sélecteur.',
  'پنل، btn-default، input-group-addon و ساختار کلاسیک': 'Panneau, btn-default, input-group-addon et structure classique',
  'Card، btn-secondary، form-check و ساختار Bootstrap 5': 'Card, btn-secondary, form-check et structure Bootstrap 5',
  'تاریخ شرط معتبر نیست؛ تاریخ را از تقویم انتخاب کنید.': 'La date de la condition n’est pas valide ; sélectionnez une date dans le calendrier.',
  '<div class="report-header"><h2>عنوان گزارش</h2></div>': '"<div class=""report-header""><h2>Titre du rapport</h2></div>"',
  'اسکریپت‌ها و رویدادهای ناامن هنگام نمایش حذف می‌شوند.': 'Les scripts et gestionnaires d’événements dangereux sont supprimés lors de l’affichage.',
  'بدون نیاز به نوشتن HTML یا CSS، رنگ متن را از پالت انتخاب کنید.': 'Choisissez la couleur du texte dans la palette sans écrire de HTML ni de CSS.',
  'پسوند (.pdf)، MIME (image/png) و الگوی MIME (image/*) پشتیبانی می‌شود.': 'Les extensions (.pdf), les types MIME (image/png) et les motifs MIME (image/*) sont pris en charge.',
  'HTML ذخیره می‌شود، اما تگ‌های اجرایی و Attributeهای خطرناک حذف می‌شوند.': 'Le HTML est enregistré, mais les balises exécutables et les attributs dangereux sont supprimés.',
  'با حرف انگلیسی شروع شود؛ این مقدار پس از ثبت پاسخ‌ها بهتر است تغییر نکند.': 'Commencez par une lettre anglaise ; évitez de modifier cette valeur après l’existence de réponses.',
  'نام فنی فرم باید با حرف انگلیسی شروع شود و فقط شامل حروف، عدد و زیرخط باشد.': 'Le nom technique du formulaire doit commencer par une lettre anglaise et contenir uniquement des lettres, des chiffres et des traits de soulignement.',
  'در خروجی فقط فضای خالی ایجاد می‌شود و خطوط راهنمای طراح چاپ یا نمایش داده نمی‌شوند.': 'Seul un espace vide est affiché ; les guides du concepteur ne sont ni affichés ni imprimés.',
  'خالی یعنی هر عدد اعشاری مجاز است؛ ۱ برای عدد صحیح و ۰٫۱ برای یک رقم اعشار مناسب است.': 'Une valeur vide autorise tous les nombres décimaux ; utilisez 1 pour les entiers ou 0,1 pour une décimale.',
  'فقط حروف انگلیسی، عدد و زیرخط مجاز است. این نام برای داده‌های ذخیره‌شده استفاده می‌شود.': 'Seuls les lettres anglaises, les chiffres et les traits de soulignement sont autorisés. Ce nom est utilisé pour les données enregistrées.',
  'تاریخ شروع شمسی معتبر نیست؛ تاریخ را از تقویم انتخاب کنید یا با قالب 1405/01/01 وارد کنید.': 'La date de début n’est pas valide ; sélectionnez-la dans le calendrier ou saisissez-la au format 21/03/2026.',
  'تاریخ پایان شمسی معتبر نیست؛ تاریخ را از تقویم انتخاب کنید یا با قالب 1405/01/01 وارد کنید.': 'La date de fin n’est pas valide ; sélectionnez-la dans le calendrier ou saisissez-la au format 21/03/2026.',
  'هر Attribute را در یک خط وارد کنید. data-* و aria-* و Attributeهای استاندارد امن قابل استفاده‌اند.': 'Saisissez un attribut par ligne. Les attributs data-*, aria-* et les attributs standard sûrs sont pris en charge.',
  'جستجوی کنترل...': 'Rechercher des contrôles…',
  'فهرست': 'Liste',
  '← فهرست': '← Liste',
  'لایه را اینجا قرار دهید': 'Déposez la mise en page ici',
  'لایه را در انتها قرار دهید': 'Placez la mise en page à la fin',
  'لایه را داخل پنل اینجا قرار دهید': 'Déposez la mise en page dans le panneau ici',
  'لایه را در بخش ورود ردیف اینجا قرار دهید': 'Déposez la mise en page dans la zone de saisie de ligne ici',
  'یک لایه از ستون ابزار اضافه کنید.': 'Ajoutez une mise en page depuis le panneau d’outils.',
  'کنترلی انتخاب نشده است': 'Aucun contrôle n’est sélectionné.',
  'لایه‌ای انتخاب نشده است': 'Aucune mise en page n’est sélectionnée.',
  'روی یک کنترل در بوم فرم کلیک کنید تا تنظیمات آن نمایش داده شود.': 'Sélectionnez un contrôle sur le canevas pour modifier ses paramètres.',
  'روی حاشیه یک لایه کلیک کنید تا تنظیمات ستون‌ها نمایش داده شود.': 'Sélectionnez la bordure d’une mise en page pour modifier ses colonnes.',
  'افزودن': 'Ajouter',
  'انتخاب': 'Sélectionner',
  'انصراف': 'Annuler',
  'پاک کردن': 'Effacer',
  'جزئیات': 'Détails',
  'عملیات': 'Actions',
  'اطلاعات': 'Informations',
  'بازیابی': 'Restaurer',
  'پیش‌فرض': 'Par défaut',
  'از تاریخ': 'Date de début',
  'تا تاریخ': 'Date de fin',
  'جدیدترین': 'Plus récent',
  'قدیمی‌ترین': 'Plus ancien',
  'حذف گروه': 'Supprimer le groupe',
  'وضعیت‌ها': 'Statuts',
  'ارسال فرم': 'Envoyer le formulaire',
  'حروف بزرگ': 'Majuscules',
  'حروف کوچک': 'Minuscules',
  'خالی باشد': 'Est vide',
  'خالی نباشد': 'N’est pas vide',
  'شامل نباشد': 'Ne contient pas',
  'شناسه فنی': 'ID technique',
  'عرض از ۱۲': 'Largeur sur 12',
  'مرحله اول': 'Première étape',
  'مرحله دوم': 'Deuxième étape',
  'مرحله قبل': 'Étape précédente',
  'مرحله بعد': 'Étape suivante',
  'گزینه اول': 'Première option',
  'افزودن شرط': 'Ajouter une condition',
  'بدون اتصال': 'Aucune liaison',
  'بدون فیلتر': 'Aucun filtre',
  'تاریخ شمسی': 'Date persane',
  'متن نمایشی': 'Texte d’affichage',
  'متن چندخطی': 'Texte multiligne',
  'همه فرم‌ها': 'Tous les formulaires',
  'ورودی عددی': 'Champ numérique',
  'ورودی متنی': 'Champ texte',
  'گروه ورودی': 'Groupe de saisie',
  'انتخاب فیلد': 'Sélectionner un champ',
  'بدون عملکرد': 'Aucune action',
  'تقسیم مساوی': 'Répartir équitablement',
  'طراحی گزارش': 'Conception du rapport',
  'فیلدهای فرم': 'Champs du formulaire',
  'زمان نوشتاری': 'Heure en toutes lettres',
  'تاریخ نوشتاری': 'Date en toutes lettres',
  'تلفن ثابت ایران': 'Téléphone fixe iranien',
  'کد ملی ایران': 'Code national iranien',
  'آدرس اینترنتی': 'Adresse web',
  'اطلاعات رکورد': 'Informations de la réponse',
  'بزرگ‌تر': 'Supérieur à',
  'کوچک‌تر': 'Inférieur à',
  'بزرگ‌تر مساوی': 'Supérieur ou égal à',
  'کوچک‌تر مساوی': 'Inférieur ou égal à',
  'بزرگ‌تر یا مساوی': 'Supérieur ou égal à',
  'کوچک‌تر یا مساوی': 'Inférieur ou égal à',
  'برابر': 'Égal à',
  'نابرابر': 'Différent de',
  'حذف این مرحله': 'Supprimer cette étape',
  'شرط‌های جستجو': 'Conditions de recherche',
  'ورودی‌های فرم': 'Champs de saisie du formulaire',
  'گزینه تیک‌دار': 'Option à cocher',
  'گزینه رادیویی': 'Option radio',
  '＋ افزودن ستون': '＋ Ajouter une colonne',
  '＋ افزودن گزینه': '＋ Ajouter une option',
  'شامل حروف باشد': 'Inclure des lettres',
  'نمایش کامل فرم': 'Aperçu complet du formulaire',
  'نمونه پیام خطا': 'Exemple de message d’erreur',
  'نمونه پیام هشدار': 'Exemple de message d’avertissement',
  'نمونه پیام موفقیت': 'Exemple de message de réussite',
  'بدون اعتبارسنجی': 'Aucune validation',
  'تصویر واکنش‌گرا': 'Image adaptative',
  'تصویر واکنش‌گرا باشد': 'Utiliser une image adaptative',
  'همیشه مخفی باشد': 'Toujours masqué',
  'گزارش پیدا نشد.': 'Rapport introuvable.',
  'افزودن گروه اصلی': 'Ajouter un groupe racine',
  'ایجاد گزارش جدید': 'Créer un nouveau rapport',
  'سازگاری Bootstrap': 'Compatibilité Bootstrap',
  'فرم‌های تکمیل‌شده': 'Formulaires complétés',
  'مجموع عرض ستون‌ها': 'Largeur totale des colonnes',
  'افزودن ستون نمایشی': 'Ajouter une colonne d’affichage',
  'بدون مقدار پیش‌فرض': 'Aucune valeur par défaut',
  'ستون‌ها و وضعیت‌ها': 'Colonnes et statuts',
  'جدول، پیام و پیشرفت': 'Tableau, message et progression',
  'بازبینی زنده کنترل‌ها': 'Aperçu en direct des contrôles',
  'غیرفعال در حالت تکمیل': 'Désactivé pendant la saisie',
  'ستون‌های داده‌ای جدول:': 'Colonnes de données du tableau :',
  'شرطی در این گروه نیست.': 'Ce groupe ne contient aucune condition.',
  'حذف ستون و انتقال محتوا': 'Supprimer la colonne et déplacer le contenu',
  'سفارشی‌سازی پایدار HTML': 'Personnalisation HTML persistante',
  'فرم مبنا را انتخاب کنید': 'Sélectionnez le formulaire source',
  'گزارش از نتایج این صفحه': 'Rapport à partir des résultats de cette page',
  'امکان بازگشت از این مرحله': 'Autoriser le retour depuis cette étape',
  'تکمیل این فیلد الزامی است': 'Ce champ est obligatoire',
  'چیدمان این لایه کامل است.': 'Cette mise en page est complète.',
  'کنترل‌های نمایشی و ترکیبی': 'Contrôles d’affichage et composites',
  'برای امنیت اعمال نمی‌شوند.': 'Ils ne sont pas appliqués pour des raisons de sécurité.',
  'توقف آنی پس از تکمیل ظرفیت': 'Arrêter immédiatement lorsque la capacité est atteinte',
  'کنترلی با این عبارت پیدا نشد.': 'Aucun contrôle ne correspond à cette recherche.',
  'پاسخی با این فیلترها پیدا نشد.': 'Aucune réponse ne correspond à ces filtres.',
  'به‌صورت پیش‌فرض انتخاب شده باشد': 'Sélectionné par défaut',
  'لایه‌ها و محتوای بخش ورود ردیف:': 'Mises en page et contenu de la zone de saisie de ligne :',
  'پیش‌نمایش اطلاعات پس از این مرحله': 'Afficher un aperçu des données après cette étape',
  'رکوردی برای گزارش انتخاب نشده است.': 'Aucune réponse n’a été sélectionnée pour le rapport.',
  'نمونه متن HTML و توضیحات داخل فرم.': 'Exemple de texte HTML et de description dans le formulaire.',
  'شرط‌ها یا بازه تاریخ را تغییر دهید.': 'Modifiez les conditions ou la plage de dates.',
  'مقدار در پاسخ‌های این فرم یکتا باشد': 'La valeur doit être unique parmi les réponses de ce formulaire',
  'تغییر انتخاب‌شده هنوز ذخیره نشده است.': 'La modification sélectionnée n’est pas encore enregistrée.',
  'نمایش کنترل وابسته به مقدار فیلد دیگری باشد': 'Afficher ce contrôle selon la valeur d’un autre champ',
  'عبارت فیلد در محل نشانگر ویرایشگر درج می‌شود.': 'L’expression du champ est insérée à la position du curseur dans l’éditeur.',
  'جستجو، فیلتر، گزارش، وضعیت و مدیریت پاسخ‌های کاربران': 'Rechercher, filtrer, produire des rapports, modifier les statuts et gérer les réponses des utilisateurs',
  'طراحی قالب‌های چاپ و نمایش اطلاعات فرم‌های تکمیل‌شده': 'Concevoir des modèles d’impression et des vues pour les formulaires complétés',
  'جعبه متنی، انتخابی، رادیو، چک‌باکس یا آپلود فایل را اینجا رها کنید': 'Déposez ici un champ texte, une liste déroulante, un bouton radio, une case à cocher ou un téléversement de fichier',
  'لایه، متن، کد امنیتی، تصویر، فاصله، دکمه یا برچسب متصل را اینجا رها کنید': 'Déposez ici une mise en page, du texte, un code de sécurité, une image, un espacement, un bouton ou un libellé lié',
  'این کنترل همیشه نمایش داده می‌شود؛ مگر اینکه گزینه «همیشه مخفی» فعال باشد.': 'Ce contrôle est toujours affiché, sauf si l’option « Toujours masqué » est activée.',
  'شرط‌ها به‌صورت داده ساخت‌یافته ذخیره می‌شوند و دیگر عبارت خام AngularJS تولید نمی‌شود.': 'Les conditions sont enregistrées sous forme de données structurées ; aucune expression AngularJS brute n’est désormais générée.',
  'این بخش جایگزین تغییر مستقیم DOM با Inspect است. تغییرات همراه فرم ذخیره می‌شوند. Attributeهای رویدادی مانند': 'Cette section remplace la modification directe du DOM avec Inspect. Les modifications sont enregistrées avec le formulaire. Les attributs d’événement tels que',
  'نسخه Bootstrap قالب این سایت را انتخاب کنید تا داشبورد، فرم عمومی، گزارش‌ها و همه کنترل‌ها با همان نسل هماهنگ شوند.': 'Sélectionnez la version Bootstrap du thème de ce site afin que le tableau de bord, les formulaires publics, les rapports et tous les contrôles restent compatibles.',
  'عرض هر ردیف در Bootstrap برابر ۱۲ واحد است. مجموع کمتر از ۱۲ فضای خالی ایجاد می‌کند و مجموع بیشتر از ۱۲ باعث شکستن ستون‌ها به خط بعد می‌شود.': 'Chaque ligne Bootstrap comporte 12 unités. Un total inférieur à 12 laisse un espace vide ; un total supérieur à 12 renvoie les colonnes à la ligne suivante.',
  'چپ': 'Gauche',
  'راست': 'Droite',
  'کوچک': 'Petit',
  'متوسط': 'Moyen',
  'بزرگ': 'Grand',
  'اصلی': 'Principal',
  'خنثی': 'Par défaut',
  'موفق': 'Succès',
  'خطر': 'Danger',
  'موبایل': 'Mobile',
  'ایمیل': 'E-mail',
  'رمز': 'Mot de passe',
  'عددی': 'Numérique',
  'چاپ': 'Imprimer',
  'عنوان مدیریتی کنترل': 'Libellé d’administration du contrôle',
  'فقط در فرم‌ساز، فهرست شرط‌ها و گزارش‌ها استفاده می‌شود و در فرم خروجی نمایش داده نمی‌شود.': 'Utilisé uniquement dans le concepteur, les listes de règles et les rapports ; il n’est pas affiché dans le formulaire public.',
  'رنگ مرحله': 'Couleur de l’étape',
  'رنگ متن مرحله': 'Couleur du texte de l’étape',
  'لایه یا کنترل فاصله را اینجا قرار دهید': 'Déposez une mise en page ou un espacement ici',
  'برای انتقال لایه یا کنترل فاصله به انتهای مرحله، آن را اینجا رها کنید.': 'Déposez la mise en page ou l’espacement ici pour le déplacer à la fin de l’étape.',
  'با فعال‌سازی این گزینه، تمام فیلدهای داده‌ای داخل پنل نیز الزامی می‌شوند.': 'Lorsque cette option est activée, tous les champs de saisie du panneau deviennent obligatoires.',
  'اجازه مشاهده داشبورد را ندارید.': 'Vous n’êtes pas autorisé à consulter le tableau de bord.',
  'ایجاد فرم برای این پورتال مجاز نیست یا ظرفیت فرم تکمیل شده است.': 'La création d’un formulaire n’est pas autorisée pour ce portail ou sa capacité de formulaires est atteinte.',
  'طراحی و ویرایش فرم برای این پورتال فقط توسط کاربر ارشد DNN مجاز است.': 'Seul un SuperUser DNN peut concevoir ou modifier les formulaires de ce portail.',
  'اجازه مشاهده پاسخ‌ها را ندارید.': 'Vous n’êtes pas autorisé à consulter les réponses.',
  'شناسه فرم یا پاسخ معتبر نیست.': 'L’identifiant du formulaire ou de la réponse n’est pas valide.',
  'بازگشت به پاسخ‌ها': 'Retour aux réponses',
  'اجازه مشاهده گزارش‌ها را ندارید.': 'Vous n’êtes pas autorisé à consulter les rapports.',
  'این بخش فقط برای کاربر ارشد DNN است.': 'Cette section est réservée aux SuperUsers DNN.',
  'اجازه مدیریت تنظیمات پیامک را ندارید.': 'Vous n’êtes pas autorisé à gérer les paramètres SMS.',
  'اجازه مدیریت سطوح دسترسی را ندارید.': 'Vous n’êtes pas autorisé à gérer les droits d’accès.',

};

const ATTRIBUTE_NAMES = ['placeholder', 'title', 'aria-label'] as const;
const PERSIAN_TEXT = /[\u0600-\u06ff]/;

function normalizeKey(value: string): string {
  return value
    .replace(/\u200c/g, '')
    .replace(/ي/g, 'ی')
    .replace(/ك/g, 'ک')
    .replace(/\s+/g, ' ')
    .trim();
}

const NORMALIZED_COPY_BY_LOCALE: Record<Exclude<UiLocale, 'fa'>, Map<string, string>> = {
  en: new Map(Object.entries(ENGLISH_COPY).map(([key, value]) => [normalizeKey(key), value])),
  fr: new Map(Object.entries(FRENCH_COPY).map(([key, value]) => [normalizeKey(key), value]))
};

function translateDynamic(value: string, locale: Exclude<UiLocale, 'fa'>): string | undefined {
  const normalized = normalizeKey(value);
  const french = locale === 'fr';
  let match = /^فرم (\d+)$/.exec(normalized);
  if (match) return french ? `Formulaire ${match[1]}` : `Form ${match[1]}`;
  match = /^مرحله (\d+)$/.exec(normalized);
  if (match) return french ? `Étape ${match[1]}` : `Step ${match[1]}`;
  match = /^مرحله (\d+) از (\d+)$/.exec(normalized);
  if (match) return french ? `Étape ${match[1]} sur ${match[2]}` : `Step ${match[1]} of ${match[2]}`;
  match = /^صفحه (\d+) از (\d+)$/.exec(normalized);
  if (match) return french ? `Page ${match[1]} sur ${match[2]}` : `Page ${match[1]} of ${match[2]}`;
  match = /^(\d+) نتیجه یافت شد\.$/.exec(normalized);
  if (match) return french ? `${match[1]} résultat${match[1] === '1' ? '' : 's'} trouvé${match[1] === '1' ? '' : 's'}.` : `${match[1]} results found.`;
  match = /^نتایج \((.+)\)$/.exec(normalized);
  if (match) return french ? `Résultats (${match[1]})` : `Results (${match[1]})`;
  match = /^(\d+) خطا · (\d+) هشدار$/.exec(normalized);
  if (match) return french
    ? `${match[1]} erreur${match[1] === '1' ? '' : 's'} · ${match[2]} avertissement${match[2] === '1' ? '' : 's'}`
    : `${match[1]} errors · ${match[2]} warnings`;
  match = /^بررسی \((\d+)\)$/.exec(normalized);
  if (match) return french ? `Vérifier (${match[1]})` : `Validate (${match[1]})`;
  match = /^فرم (\d+) · ذخیرهشده$/.exec(normalized);
  if (match) return french ? `Formulaire ${match[1]} · Enregistré` : `Form ${match[1]} · Saved`;
  match = /^فرم (\d+) · ذخیرهنشده$/.exec(normalized);
  if (match) return french ? `Formulaire ${match[1]} · Non enregistré` : `Form ${match[1]} · Unsaved`;
  match = /^فرم جدید · ذخیرهشده$/.exec(normalized);
  if (match) return french ? 'Nouveau formulaire · Enregistré' : 'New form · Saved';
  match = /^فرم جدید · ذخیرهنشده$/.exec(normalized);
  if (match) return french ? 'Nouveau formulaire · Non enregistré' : 'New form · Unsaved';
  match = /^مرحله:\s*(.+)$/.exec(normalized);
  if (match) return french ? `Étape : ${match[1]}` : `Step: ${match[1]}`;
  match = /^مجموع عرض (\d+) از ۱۲$/.exec(normalized);
  if (match) return french ? `Largeur totale ${match[1]} sur 12` : `Total span ${match[1]} of 12`;
  match = /^(\d+) واحد فضای خالی باقی مانده است\.$/.exec(normalized);
  if (match) return french ? `${match[1]} unité${match[1] === '1' ? '' : 's'} de grille reste${match[1] === '1' ? '' : 'nt'} inutilisée${match[1] === '1' ? '' : 's'}.` : `${match[1]} unused grid units remain.`;
  match = /^(\d+) واحد اضافه است و ممکن است ستونها شکسته شوند\.$/.exec(normalized);
  if (match) return french ? `${match[1]} unité${match[1] === '1' ? '' : 's'} supplémentaire${match[1] === '1' ? '' : 's'} peut${match[1] === '1' ? '' : 'vent'} renvoyer les colonnes à la ligne.` : `${match[1]} extra grid units may wrap columns.`;
  match = /^گزینه (\d+)$/.exec(normalized);
  if (match) return french ? `Option ${match[1]}` : `Option ${match[1]}`;
  match = /^وضعیت (\d+)$/.exec(normalized);
  if (match) return french ? `Statut ${match[1]}` : `Status ${match[1]}`;
  match = /^عنوان وضعیت (\d+)$/.exec(normalized);
  if (match) return french ? `Titre du statut ${match[1]}` : `Status title ${match[1]}`;
  match = /^حذف گزینه (\d+)$/.exec(normalized);
  if (match) return french ? `Supprimer l’option ${match[1]}` : `Delete option ${match[1]}`;
  match = /^نمایش (XS|SM|MD|LG)$/i.exec(normalized);
  if (match?.[1]) return french ? `Aperçu ${match[1].toUpperCase()}` : `${match[1].toUpperCase()} preview`;
  match = /^(\d+) ستون؛ کلیک یا درگ$/.exec(normalized);
  if (match) return french ? `${match[1]} colonne${match[1] === '1' ? '' : 's'} ; cliquer ou faire glisser` : `${match[1]} columns; click or drag`;
  match = /^لایه (\d+) ستونه$/.exec(normalized);
  if (match) return french ? `Mise en page à ${match[1]} colonne${match[1] === '1' ? '' : 's'}` : `${match[1]}-column layout`;
  match = /^(\d+) ستون$/.exec(normalized);
  if (match) return french ? `${match[1]} colonne${match[1] === '1' ? '' : 's'}` : `${match[1]} ${match[1] === '1' ? 'column' : 'columns'}`;
  match = /^حداکثر (\d+) ستون؛ ترتیب فهرست زیر همان ترتیب جدول خواهد بود\.$/.exec(normalized);
  if (match) return french ? `Jusqu’à ${match[1]} colonnes ; l’ordre de la liste sera celui du tableau.` : `Up to ${match[1]} columns; the list order is the table order.`;
  match = /^انتقال (.+) به بالا$/.exec(normalized);
  if (match) return french ? `Monter ${match[1]}` : `Move ${match[1]} up`;
  match = /^انتقال (.+) به پایین$/.exec(normalized);
  if (match) return french ? `Descendre ${match[1]}` : `Move ${match[1]} down`;
  match = /^حذف ستون (.+)$/.exec(normalized);
  if (match) return french ? `Retirer la colonne ${match[1]}` : `Remove ${match[1]} column`;
  match = /^(.+)؛ کلیک یا درگ$/.exec(normalized);
  if (match?.[1]) return french
    ? `${translateCore(match[1], locale) ?? match[1]} ; cliquer ou faire glisser`
    : `${translateCore(match[1], locale) ?? match[1]}; click or drag`;
  match = /^این مرحله دارای (\d+) کنترل یا لایه است\. مرحله حذف شود؟$/.exec(normalized);
  if (match) return french ? `Cette étape contient ${match[1]} contrôles ou mises en page. Supprimer l’étape ?` : `This step contains ${match[1]} controls or layouts. Delete the step?`;
  match = /^فرم ذخیره شد؛ (\d+) هشدار باقی مانده است\.$/.exec(normalized);
  if (match) return french ? `Formulaire enregistré avec ${match[1]} avertissement${match[1] === '1' ? '' : 's'} restant${match[1] === '1' ? '' : 's'}.` : `Form saved with ${match[1]} remaining warnings.`;
  match = /^(\d+) خطا مانع ذخیره فرم است\.$/.exec(normalized);
  if (match) return french ? `${match[1]} erreur${match[1] === '1' ? '' : 's'} empêche${match[1] === '1' ? '' : 'nt'} l’enregistrement du formulaire.` : `${match[1]} errors prevent saving the form.`;
  return undefined;
}

function translateCore(core: string, locale: UiLocale): string | undefined {
  if (locale === 'fa') return undefined;
  const normalized = normalizeKey(core);
  return NORMALIZED_COPY_BY_LOCALE[locale].get(normalized) ?? translateDynamic(core, locale);
}

export function translateManagementText(value: string, locale: UiLocale): string {
  if (locale === 'fa' || !PERSIAN_TEXT.test(value)) return value;

  // Exact phrases may intentionally include final punctuation. Try the full
  // value before peeling UI decoration so dictionary entries such as the
  // palette help text are not made unreachable by punctuation stripping.
  const exact = translateCore(value, locale);
  if (exact) return exact;

  const leading = value.match(/^[\s←→◉＋+↻↶↷✓×✕•]+/)?.[0] ?? '';
  const trailing = value.match(/[\s:*؛،,.!?؟…]+$/)?.[0] ?? '';
  const core = value.slice(leading.length, value.length - trailing.length);
  const translated = translateCore(core, locale);
  return translated ? `${leading}${translated}${trailing}` : value;
}

function translateText(value: string, locale: UiLocale): string {
  return translateManagementText(value, locale);
}

interface AttributeRecord {
  name: string;
  original: string;
}

export function useManagementDomI18n(
  rootRef: RefObject<HTMLElement | null>,
  locale: UiLocale,
  scopeKey = ''
) {
  const textRecords = useRef(new Map<Text, string>());
  const attributeRecords = useRef(new Map<Element, Map<string, AttributeRecord>>());

  useLayoutEffect(() => {
    if (typeof document === 'undefined') return;

    let disposed = false;
    let frame = 0;
    let translating = false;
    let observer: MutationObserver | undefined;
    let retryTimer = 0;
    let safetyTimer = 0;

    const resolveRoot = (): HTMLElement | undefined => rootRef.current
      ?? document.querySelector<HTMLElement>('.df-admin-route-content')
      ?? document.querySelector<HTMLElement>('[data-dynamicforms-react-root][data-mode="management"]')
      ?? undefined;

    const cleanupDisconnectedRecords = () => {
      textRecords.current.forEach((_original, node) => {
        if (!node.isConnected) textRecords.current.delete(node);
      });
      attributeRecords.current.forEach((_records, element) => {
        if (!element.isConnected) attributeRecords.current.delete(element);
      });
    };

    const restore = () => {
      translating = true;
      textRecords.current.forEach((original, node) => {
        if (node.isConnected) node.nodeValue = original;
      });
      attributeRecords.current.forEach((records, element) => {
        if (!element.isConnected) return;
        records.forEach(({ name, original }) => element.setAttribute(name, original));
      });
      textRecords.current.clear();
      attributeRecords.current.clear();
      const root = resolveRoot();
      if (root) {
        root.dataset.dfDomI18nLocale = 'fa';
        root.dataset.dfDomI18nVersion = '3.3.1';
      }
      translating = false;
    };

    const translateNode = (node: Text) => {
      const current = node.nodeValue ?? '';
      let original = textRecords.current.get(node);
      if (original !== undefined) {
        const expected = translateText(original, locale);
        if (current !== expected && PERSIAN_TEXT.test(current)) {
          original = current;
          textRecords.current.set(node, current);
        }
      } else {
        original = current;
      }
      const translated = translateText(original, locale);
      if (translated !== original) {
        if (!textRecords.current.has(node)) textRecords.current.set(node, original);
        if (current !== translated) node.nodeValue = translated;
      }
    };

    const translateElement = (element: Element) => {
      for (const name of ATTRIBUTE_NAMES) {
        const current = element.getAttribute(name);
        if (!current) continue;
        let records = attributeRecords.current.get(element);
        const existing = records?.get(name);
        let original = existing?.original ?? current;
        if (existing) {
          const expected = translateText(existing.original, locale);
          if (current !== expected && PERSIAN_TEXT.test(current)) {
            original = current;
            existing.original = current;
          }
        }
        const translated = translateText(original, locale);
        if (translated !== original) {
          if (!records) {
            records = new Map();
            attributeRecords.current.set(element, records);
          }
          if (!records.has(name)) records.set(name, { name, original });
          if (current !== translated) element.setAttribute(name, translated);
        }
      }
    };

    const apply = () => {
      if (disposed || locale === 'fa') return;
      const root = resolveRoot();
      if (!root) return;
      translating = true;
      const walker = document.createTreeWalker(root, NodeFilter.SHOW_ELEMENT | NodeFilter.SHOW_TEXT);
      let node: Node | null = walker.currentNode;
      while (node) {
        if (node.nodeType === Node.TEXT_NODE) translateNode(node as Text);
        else if (node.nodeType === Node.ELEMENT_NODE) translateElement(node as Element);
        node = walker.nextNode();
      }
      root.dataset.dfDomI18nLocale = locale;
      root.dataset.dfDomI18nVersion = '3.3.1';
      cleanupDisconnectedRecords();
      translating = false;
    };

    const schedule = () => {
      if (disposed || translating || locale === 'fa') return;
      if (frame) window.cancelAnimationFrame(frame);
      frame = window.requestAnimationFrame(() => {
        frame = 0;
        apply();
      });
    };

    const attach = () => {
      if (disposed || locale === 'fa') return;
      const root = resolveRoot();
      if (!root) {
        retryTimer = window.setTimeout(attach, 30);
        return;
      }
      apply();
      observer = new MutationObserver(() => schedule());
      observer.observe(document.body, {
        subtree: true,
        childList: true,
        characterData: true,
        attributes: true,
        attributeFilter: [...ATTRIBUTE_NAMES]
      });
    };

    const handleVisibility = () => {
      if (document.visibilityState === 'visible') schedule();
    };

    if (locale === 'fa') {
      restore();
      return;
    }

    attach();
    schedule();
    for (const delay of [0, 32, 120, 350, 900, 2000, 5000]) {
      window.setTimeout(schedule, delay);
    }
    window.addEventListener('focus', schedule);
    window.addEventListener('pageshow', schedule);
    window.addEventListener('hashchange', schedule);
    document.addEventListener('visibilitychange', handleVisibility);
    safetyTimer = window.setInterval(schedule, 750);
    (window as Window & { __DF_MANAGEMENT_I18N_REVISION__?: string }).__DF_MANAGEMENT_I18N_REVISION__ = '3.3.1';

    return () => {
      disposed = true;
      observer?.disconnect();
      if (frame) window.cancelAnimationFrame(frame);
      if (retryTimer) window.clearTimeout(retryTimer);
      if (safetyTimer) window.clearInterval(safetyTimer);
      window.removeEventListener('focus', schedule);
      window.removeEventListener('pageshow', schedule);
      window.removeEventListener('hashchange', schedule);
      document.removeEventListener('visibilitychange', handleVisibility);
    };
  }, [locale, rootRef, scopeKey]);
}

